import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AccountBalance,
  BusinessCenter,
  CheckCircle,
  Close,
  ContactSupport,
  Diversity3,
  Edit,
  EventAvailable,
  Gavel,
  KeyboardArrowLeft,
  Lightbulb,
  Login,
  Psychology,
  RestartAlt,
  School,
  TrendingUp,
  VolunteerActivism,
} from "@mui/icons-material";

import { AuthContext } from "../../../../context/AuthContext";
import "../../styles/opportunity-companion.css";
import { buildMentorPathwayReport, buildOpportunityAssessment } from "./opportunityAssessment";
import {
  V2_QUESTIONS,
  V2_SECTOR_EXPLORATION,
  updateMultiSelectAnswer,
} from "./opportunityCompanionV2";
import {
  getEffectiveInputValue,
  safeStorageGet,
  safeStorageSet,
  validateReportRecipient,
} from "../../utils/opportunityCompanionControls";

const PopupScrollRail = ({ scrollRef }) => {
  const [scrollState, setScrollState] = useState({ visible: false, size: 100, offset: 0 });

  useEffect(() => {
    let target;
    let resizeObserver;
    let frame;

    const updateScrollState = () => {
      if (!target) return;
      const scrollableHeight = target.scrollHeight - target.clientHeight;
      if (scrollableHeight <= 1) {
        setScrollState({ visible: false, size: 100, offset: 0 });
        return;
      }

      const size = Math.max(14, Math.min(82, (target.clientHeight / target.scrollHeight) * 100));
      const progress = Math.min(1, Math.max(0, target.scrollTop / scrollableHeight));
      setScrollState({ visible: true, size, offset: progress * (100 - size) });
    };

    frame = window.requestAnimationFrame(() => {
      target = scrollRef.current;
      if (!target) return;
      updateScrollState();
      target.addEventListener("scroll", updateScrollState, { passive: true });
      window.addEventListener("resize", updateScrollState);
      if (window.ResizeObserver) {
        resizeObserver = new window.ResizeObserver(updateScrollState);
        resizeObserver.observe(target);
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (target) target.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      resizeObserver?.disconnect();
    };
  }, [scrollRef]);

  if (!scrollState.visible) return null;

  return (
    <span className="uc-popup-scroll-rail" aria-hidden="true">
      <span
        className="uc-popup-scroll-thumb"
        style={{ height: `${scrollState.size}%`, top: `${scrollState.offset}%` }}
      />
    </span>
  );
};

const SECTORS = {
  insurance: {
    label: "Insurance",
    route: "insurance",
    color: "#1976d2",
    income: "$200–$1,200",
    timeline: "4–10 weeks",
    risk: "Medium",
    energy: "Medium",
    paths: [
      "Insurance awareness/referral support with licensed professionals",
      "Community policy education sessions",
      "Family protection readiness workshops",
    ],
  },
  healthcare: {
    label: "Healthcare",
    route: "healthcare",
    color: "#e53935",
    income: "$200–$1,200",
    timeline: "3–10 weeks",
    risk: "Medium",
    energy: "Medium",
    paths: [
      "Senior companionship or non-medical community support",
      "Healthy Indian meal-prep or wellness cooking support",
      "Yoga, Ayurveda, naturopathy, or wellness awareness workshops",
    ],
  },
  education: {
    label: "Education",
    route: "education",
    color: "#8e24aa",
    income: "$150–$1,200",
    timeline: "2–6 weeks",
    risk: "Low–Medium",
    energy: "Medium",
    paths: [
      "Tutoring, language, or cultural classes for children",
      "Art, music, dance, storytelling, or public-speaking micro-classes",
      "Cooking, culture, shloka, or festival-learning workshops",
    ],
  },
  small_industries: {
    label: "Small Industries",
    route: "small-industries",
    color: "#f57c00",
    income: "$300–$2,000",
    timeline: "2–12 weeks",
    risk: "Medium–High",
    energy: "Medium–High",
    paths: [
      "Tiffin, catering, baking, sweets, snacks, or home-food micro-business",
      "Festival kits, pooja material kits, or community product boxes",
      "Home product, reselling, or family-supported small business",
    ],
  },
  research: {
    label: "Research",
    route: "research",
    color: "#43a047",
    income: "$150–$800",
    timeline: "4–10 weeks",
    risk: "Low–Medium",
    energy: "Low–Medium",
    paths: [
      "Community survey/research support",
      "Data collection or online research assistance",
      "Behind-the-scenes analysis and documentation work",
    ],
  },
  travel: {
    label: "Travel",
    route: "travel",
    color: "#00acc1",
    income: "$250–$1,500",
    timeline: "4–10 weeks",
    risk: "Medium",
    energy: "Medium–High",
    paths: [
      "Temple, pooja, festival, or cultural event coordination",
      "Group travel, airport/newcomer support, or senior ride coordination",
      "Pilgrimage, community outing, or event logistics support",
    ],
  },
  real_estate: {
    label: "Real Estate",
    route: "real-estate",
    color: "#6d4c41",
    income: "$300–$2,000",
    timeline: "4–12 weeks",
    risk: "Medium",
    energy: "Medium",
    paths: [
      "Rental/home-search orientation support",
      "Property documentation and showing coordination support",
      "Newcomer relocation guidance with proper boundaries",
    ],
  },
  finance: {
    label: "Finance",
    route: "finance",
    color: "#2e7d32",
    income: "$200–$1,000",
    timeline: "4–10 weeks",
    risk: "Medium",
    energy: "Low–Medium",
    paths: [
      "Budget readiness and small-business cost planning",
      "Bookkeeping organization support",
      "Family finance awareness with professional referral where needed",
    ],
  },
  banking: {
    label: "Banking",
    route: "banking",
    color: "#3949ab",
    income: "$150–$800",
    timeline: "4–10 weeks",
    risk: "Medium",
    energy: "Low–Medium",
    paths: [
      "Newcomer banking basics orientation",
      "Document organization support",
      "Small-business banking readiness education",
    ],
  },
  legal: {
    label: "Legal",
    route: "legal",
    color: "#5e35b1",
    income: "$150–$1,000",
    timeline: "4–12 weeks",
    risk: "Medium",
    energy: "Low–Medium",
    paths: [
      "Business setup checklist support with qualified legal routing",
      "Permit/compliance awareness for home businesses",
      "Document preparation coordination without legal advice",
    ],
  },
  it: {
    label: "IT",
    route: "it",
    color: "#546e7a",
    income: "$200–$1,500",
    timeline: "4–12 weeks",
    risk: "Low–Medium",
    energy: "Low–Medium",
    paths: [
      "Website, booking form, or social media setup for community businesses",
      "Online class setup for tutors, artists, wellness educators, or priests",
      "Data entry, automation, and digital support services",
    ],
  },
};

const SUPPORT_LABELS = {
  mentor_support: "Mentor support",
  training_support: "Training support",
  enterprise_connection: "Enterprise connection",
  finance_support: "Finance readiness",
  insurance_support: "Insurance awareness",
  legal_support: "Legal/business setup routing",
  community_support: "Community introductions",
  low_investment: "Low-investment start",
  low_risk_path: "Low-risk first step",
  behind_scenes: "Behind-the-scenes work",
  family_priority: "Family-first planning",
  action_ready: "Ready-to-start path",
  student_mentor: "Student mentor match",
  internship_match: "Internship/project match",
  immigration_routing: "Immigration attorney routing",
  law_firm_partner: "Law-firm partner review",
  youth_safety: "Youth-safe supervised matching",
  licensed_partner: "Licensed/professional partner",
};

const SUPPORT_TO_SECTOR = {
  finance_support: "finance",
  insurance_support: "insurance",
  legal_support: "legal",
  training_support: "education",
  enterprise_connection: "small_industries",
  community_support: "travel",
  mentor_support: "education",
  student_mentor: "education",
  internship_match: "it",
  immigration_routing: "legal",
  law_firm_partner: "legal",
  youth_safety: "education",
  licensed_partner: "legal",
};

const INTENT_OPTIONS = [
  {
    id: "second_income",
    label: "Find a second-income or business idea",
    shortLabel: "Second-income idea",
    description: "Get matched to a practical UC sector and a small first step.",
    Icon: BusinessCenter,
    score: { action_ready: 1, mentor_support: 1 },
    audience: "Opportunity seeker",
  },
  {
    id: "student_internship",
    label: "Find an internship or career mentor",
    shortLabel: "Student / intern path",
    description: "For students, recent grads, and families seeking guided career exposure.",
    Icon: School,
    score: { education: 2, it: 1, research: 1, student_mentor: 2, internship_match: 2 },
    audience: "Student or early-career learner",
  },
  {
    id: "immigration_help",
    label: "Connect with an immigration lawyer",
    shortLabel: "Immigration help",
    description: "O-1, NIW, EB-1A and related attorney-routing intake, without legal advice.",
    Icon: Gavel,
    score: { legal: 3, research: 1, finance_support: 1, immigration_routing: 3 },
    audience: "Immigration-help seeker",
  },
  {
    id: "mentor_provider",
    label: "Mentor students or offer internships",
    shortLabel: "Mentor / internship provider",
    description: "Help young talent through mentoring, projects, internships, or talks.",
    Icon: Diversity3,
    score: { education: 2, it: 1, enterprise_connection: 2, student_mentor: 2, internship_match: 2, youth_safety: 1 },
    audience: "Community leader or mentor",
  },
  {
    id: "community_meetup",
    label: "Attend a free UC community meetup",
    shortLabel: "Community meetup",
    description: "Find a city-center, library, or online UC session and register.",
    Icon: EventAvailable,
    score: { community_support: 3, education: 1, mentor_support: 1, action_ready: 1 },
    audience: "Public meetup attendee",
  },
  {
    id: "law_firm_partner",
    label: "Become a UC law-firm partner",
    shortLabel: "Law-firm partner",
    description: "For law firms interested in ethical, transparent UC community routing.",
    Icon: AccountBalance,
    score: { legal: 4, law_firm_partner: 3, licensed_partner: 2, enterprise_connection: 1 },
    audience: "Licensed legal-service partner",
  },
  {
    id: "family_guidance",
    label: "Get family, finance, health, or education guidance",
    shortLabel: "Family-life guidance",
    description: "Explore family needs, protection, learning, healthcare, and planning support.",
    Icon: VolunteerActivism,
    score: { finance: 1, insurance: 1, healthcare: 1, education: 1, family_priority: 2 },
    audience: "Family-first planner",
  },
  {
    id: "unsure",
    label: "I’m not sure — help me explore",
    shortLabel: "Help me explore",
    description: "Start with a gentle assessment and let UC suggest a direction.",
    Icon: Lightbulb,
    score: { mentor_support: 2, training_support: 2, low_risk_path: 1 },
    audience: "Explorer",
  },
];

const INTENT_QUESTION = {
  id: "intent",
  eyebrow: "Today’s visit",
  text: "What made you come to Udyog Connect today?",
  options: INTENT_OPTIONS.map((intent) => ({
    label: intent.label,
    intentId: intent.id,
    score: intent.score,
    reason: `You came to UC for: ${intent.shortLabel}.`,
  })),
};

// This gating question is intentionally separate from sector interest. Family
// support answers must never be treated as evidence of business capability.
const GATING_QUESTION = {
  id: "intent",
  eyebrow: "Choose your UC path",
  text: "Are you looking for help with your own family and life planning, an income opportunity, or both?",
  options: [
    { label: "Family and life guidance only", intentId: "family_guidance", score: { family_priority: 1 }, reason: "You selected personal and family guidance." },
    { label: "Supplemental-income ideas only", intentId: "second_income", score: { action_ready: 1 }, reason: "You selected supplemental-income exploration." },
    { label: "Both guidance and income ideas", intentId: "mixed", score: { family_priority: 1, action_ready: 1 }, reason: "You selected separate guidance and income pathways." },
    { label: "Business-growth support", intentId: "business_growth", score: { enterprise_connection: 1 }, reason: "You selected existing-business growth support." },
    { label: "I am not sure", intentId: "unsure", score: { mentor_support: 1 }, reason: "You are still deciding which UC path fits." },
  ],
};

const BASE_QUESTIONS = [
  {
    id: "interest",
    eyebrow: "Interest",
    text: "What area are you most curious about today?",
    options: [
      { label: "Healthcare", score: { healthcare: 3 }, reason: "You showed interest in health or wellness." },
      { label: "Education", score: { education: 3 }, reason: "You showed interest in teaching or learning." },
      { label: "Small Industries", score: { small_industries: 3 }, reason: "You showed interest in small business or products." },
      { label: "Finance", score: { finance: 3, banking: 1, finance_support: 1 }, reason: "You showed interest in money planning or business readiness." },
      { label: "Banking", score: { banking: 3, finance: 2, finance_support: 1 }, reason: "You showed interest in banking operations or financial access." },
      { label: "Insurance", score: { insurance: 3, insurance_support: 1 }, reason: "You showed interest in protection and risk awareness." },
      { label: "Travel / Events", score: { travel: 3 }, reason: "You showed interest in coordination, events, or movement." },
      { label: "Real Estate", score: { real_estate: 3 }, reason: "You showed interest in property or relocation support." },
      { label: "IT / Research", score: { it: 2, research: 2 }, reason: "You showed interest in digital or research work." },
      { label: "Legal", score: { legal: 3, legal_support: 1, licensed_partner: 1 }, reason: "You showed interest in legal education or qualified referral routing." },
      { label: "Research", score: { research: 3, it: 1 }, reason: "You showed interest in research, evidence, or analysis." },
      { label: "Not sure yet", score: { mentor_support: 1, training_support: 1 }, reason: "You are still exploring, so a guided path may help." },
    ],
  },
  {
    id: "style",
    eyebrow: "Income style",
    text: "What kind of extra income path sounds most comfortable?",
    options: [
      { label: "Work from home", score: { education: 2, it: 2, research: 1, behind_scenes: 1 }, reason: "You prefer a path that can begin from home." },
      { label: "Local/community service", score: { healthcare: 2, travel: 1, insurance: 1, real_estate: 1, community_support: 1 }, reason: "You may enjoy community-centered work." },
      { label: "Online or digital work", score: { it: 3, research: 2, education: 1 }, reason: "You prefer digital or online-friendly work." },
      { label: "Product selling/reselling", score: { small_industries: 3, finance_support: 1 }, reason: "You may fit a product or micro-business path." },
      { label: "Teaching/coaching", score: { education: 3, training_support: 1 }, reason: "You may enjoy helping others learn." },
      { label: "Temple/event/spiritual support", score: { travel: 2, education: 2, small_industries: 1, community_support: 2 }, reason: "You may fit cultural, spiritual, or event support work." },
      { label: "Start a small business", score: { small_industries: 3, finance_support: 1, legal_support: 1 }, reason: "You are open to building a small business." },
    ],
  },
  {
    id: "skill",
    eyebrow: "Skill or interest",
    text: "Which skill or interest would you like UC to consider?",
    options: [
      { label: "Cooking / food", score: { small_industries: 3, healthcare: 1 }, reason: "Food skills can become tiffin, meal-prep, catering, or class ideas." },
      { label: "Baking / sweets / snacks", score: { small_industries: 3, travel: 1 }, reason: "Baking and snacks can fit festival boxes or local micro-business ideas." },
      { label: "Tiffin / catering", score: { small_industries: 3, healthcare: 1, travel: 1 }, reason: "Tiffin or catering can serve families, seniors, students, and events." },
      { label: "Pooja / priest / temple support", score: { education: 2, travel: 2, small_industries: 1, community_support: 2 }, reason: "Spiritual and temple support can become community service or coordination paths." },
      { label: "Art / craft / painting", score: { education: 3, small_industries: 1 }, reason: "Creative skills can become art classes, workshops, or festival projects." },
      { label: "Music / dance / culture", score: { education: 3, travel: 1, small_industries: 1 }, reason: "Music and culture skills can become lessons, camps, or community programs." },
      { label: "Yoga / Ayurveda / naturopathy", score: { healthcare: 2, education: 2, legal_support: 1 }, reason: "Wellness knowledge can become education workshops with clear health boundaries." },
      { label: "Technology / data", score: { it: 3, research: 2 }, reason: "Technology skills can support online services and community businesses." },
      { label: "Admin / finance paperwork", score: { finance: 3, banking: 2, legal: 1 }, reason: "Organization skills can support finance, banking, documents, and small businesses." },
      { label: "Still discovering", score: { mentor_support: 2, training_support: 2 }, reason: "You may benefit from a mentor and a low-pressure learning path." },
    ],
  },
  {
    id: "time",
    eyebrow: "Time",
    text: "How much time can you give weekly in the beginning?",
    options: [
      { label: "2–5 hours", score: { low_risk_path: 2 }, snapshot: { time: "2–5 hrs/week" }, reason: "You may need a small first step." },
      { label: "5–10 hours", score: { action_ready: 1 }, snapshot: { time: "5–10 hrs/week" }, reason: "You have enough time to test a path steadily." },
      { label: "10–20 hours", score: { action_ready: 2, enterprise_connection: 1 }, snapshot: { time: "10–20 hrs/week" }, reason: "You have meaningful time to build momentum." },
      { label: "20+ hours", score: { action_ready: 3, enterprise_connection: 2 }, snapshot: { time: "20+ hrs/week" }, reason: "You may be ready for a stronger entrepreneurship path." },
    ],
  },
  {
    id: "investment",
    eyebrow: "Investment",
    text: "What starting investment feels comfortable?",
    options: [
      { label: "No investment", score: { low_investment: 3, education: 1, it: 1, research: 1 }, snapshot: { money: "$0–$50" }, reason: "You prefer a very low-cost start." },
      { label: "Under $500", score: { low_investment: 2, education: 1, small_industries: 1, travel: 1 }, snapshot: { money: "$50–$500" }, reason: "You can test a modest small start." },
      { label: "$500–$2,000", score: { small_industries: 2, real_estate: 1 }, snapshot: { money: "$500–$2,000" }, reason: "You may be able to support a small-business launch." },
      { label: "$2,000+", score: { small_industries: 2, real_estate: 2, travel: 1, finance_support: 1 }, snapshot: { money: "$2,000+" }, reason: "You may be ready for a more structured business path." },
      { label: "Need guidance", score: { finance_support: 3, mentor_support: 1 }, snapshot: { money: "Needs planning" }, reason: "You want financial clarity before starting." },
    ],
  },
  {
    id: "mindset",
    eyebrow: "Comfort",
    text: "When starting something new, what makes it easier for you?",
    options: [
      { label: "Learning quietly first", score: { behind_scenes: 2, training_support: 1, research: 1 }, reason: "You may prefer a calm learning-first path." },
      { label: "Step-by-step mentor guidance", score: { mentor_support: 3 }, reason: "A mentor-led path may help you feel supported." },
      { label: "Trying a small task first", score: { low_risk_path: 2 }, reason: "A small first test may fit your comfort level." },
      { label: "Seeing someone else succeed", score: { community_support: 1, training_support: 1 }, reason: "Examples and community stories may motivate you." },
      { label: "Working with a trusted group", score: { community_support: 2, travel: 1, education: 1 }, reason: "A group or community setting may fit you well." },
    ],
  },
  {
    id: "people",
    eyebrow: "People comfort",
    text: "How comfortable are you speaking with new people for work?",
    options: [
      { label: "Very comfortable", score: { travel: 2, real_estate: 2, insurance: 2 }, reason: "People-facing opportunities may fit you." },
      { label: "Comfortable if UC introduces me", score: { mentor_support: 1, enterprise_connection: 1, community_support: 1 }, reason: "Warm UC introductions may help you start." },
      { label: "Prefer phone/chat first", score: { it: 1, education: 1, mentor_support: 1 }, reason: "A phone/chat-first path may reduce pressure." },
      { label: "Prefer behind-the-scenes work", score: { it: 2, research: 2, finance: 1, banking: 1, behind_scenes: 3 }, reason: "Behind-the-scenes roles may fit your comfort." },
      { label: "Not sure yet", score: { mentor_support: 1, low_risk_path: 1 }, reason: "You may benefit from trying a low-pressure first step." },
    ],
  },
  {
    id: "goal",
    eyebrow: "Goal",
    text: "What is your main goal right now?",
    options: [
      { label: "Extra monthly income", score: { action_ready: 1 }, reason: "You want practical extra income." },
      { label: "Build a long-term business", score: { small_industries: 1, finance_support: 1, legal_support: 1, enterprise_connection: 2 }, reason: "You may need a path that can grow over time." },
      { label: "Learn a new skill", score: { training_support: 3, education: 1 }, reason: "Training should be part of your next step." },
      { label: "Support family needs", score: { family_priority: 2, finance_support: 1, insurance_support: 1 }, reason: "Family stability should be considered in the plan." },
      { label: "Connect with enterprises", score: { enterprise_connection: 3 }, reason: "You may be ready for UC partner or enterprise routing later." },
      { label: "Find mentor/community support", score: { mentor_support: 2, community_support: 2 }, reason: "Guidance and trusted community support matter to you." },
    ],
  },
];

const TIME_QUESTION = BASE_QUESTIONS.find((question) => question.id === "time");

const SECTOR_DOMAIN_PROMPTS = {
  healthcare: ["What experience do you already have supporting people with health or wellness logistics?", "Which safety or professional boundary are you most comfortable maintaining?"],
  education: ["What subject, age group, or learning activity have you actually taught or supported?", "What evidence could you show: lesson, class, mentoring, credential, or learner feedback?"],
  small_industries: ["Which product or service have you made, sold, tested, or helped operate?", "What customer, food-safety, licensing, or fulfillment experience do you already have?"],
  finance: ["Which finance task have you actually performed: bookkeeping, budgeting, documents, or analysis?", "What privacy, compliance, or professional boundary would you need before helping others?"],
  insurance: ["What insurance experience do you have: claims paperwork, customer service, education, or licensing?", "Which state-licensing or privacy requirement would need validation first?"],
  travel: ["Which coordination task have you handled: schedules, vendors, people, or events?", "What local community or cultural event experience can you demonstrate?"],
  real_estate: ["Which housing or relocation task have you actually supported: research, documents, or coordination?", "Which licensed-broker boundary must remain clear in your role?"],
  it_research: ["Which digital, data, website, or research project can you demonstrate?", "What tool, portfolio item, or project evidence could you share?"],
  banking: ["Which banking task have you actually handled: operations, documents, customer service, or analysis?", "What privacy, compliance, or licensing boundary needs validation first?"],
  legal: ["Which legal, policy, contract, or immigration-support experience can you demonstrate?", "What must be reserved for a licensed attorney or qualified professional?"],
  research: ["Which research question, dataset, experiment, or evidence project have you completed?", "What method, tool, publication, or portfolio evidence could you share?"],
  transportation: ["Which transportation or logistics activity have you actually coordinated?", "Which vehicle, insurance, safety, or commercial boundary needs validation before you offer it?"],
};

const SECTOR_DOMAIN_OPTIONS = {
  insurance: [
    ["Claims or policy paperwork", "Customer-service experience", "Insurance education only", "No insurance experience yet"],
    ["I need licensing guidance first", "I can explain general information, not advise", "I need privacy/compliance training", "I need a qualified-agent referral"],
  ],
  finance: [["Bookkeeping or budgeting", "Finance-document organization", "Financial-literacy education", "No finance experience yet"], ["I need privacy/compliance guidance", "Education only—no individualized advice", "I need professional supervision", "I need a qualified referral"]],
  banking: [["Bank operations or customer service", "Account/document support", "Banking-literacy education", "No banking experience yet"], ["I need bank compliance training", "I can support documents only", "I need privacy guidance", "I need a qualified referral"]],
  legal: [["Legal administration or document organization", "Policy or research support", "Community legal education", "No legal-support experience yet"], ["I need attorney-supervision boundaries", "I can provide non-legal admin support", "I need confidentiality guidance", "I need a qualified attorney referral"]],
  research: [["Research project or dataset", "Evidence/document review", "Survey or community research", "No research experience yet"], ["I need methods training", "I can use a portfolio/project example", "I need data-privacy guidance", "I need a research mentor"]],
  education: [["Tutoring or homework support", "Art, music, or language teaching", "Mentoring or classroom support", "No teaching experience yet"], ["I need child-safety guidance", "I need lesson-planning training", "I can show learner feedback", "I need a teaching mentor"]],
  healthcare: [["Care logistics or appointment support", "Senior/family companionship", "Wellness education", "No healthcare-support experience yet"], ["I need safety training", "I will not provide medical advice", "I need privacy guidance", "I need a licensed referral"]],
  small_industries: [["Product or service already tested", "Cooking, baking, or meal preparation", "Selling, fulfillment, or customer service", "No business experience yet"], ["I need food-safety/licensing guidance", "I need pricing and cost training", "I have customer feedback", "I need a business mentor"]],
  travel: [["Event or temple coordination", "Schedules, vendors, or logistics", "Community hosting or guest support", "No coordination experience yet"], ["I need safety/process guidance", "I can show an event example", "I need a local partner", "I need a coordinator mentor"]],
  real_estate: [["Housing research or relocation support", "Property documents or scheduling", "Newcomer settling support", "No housing-support experience yet"], ["I need broker-boundary guidance", "I can provide admin support only", "I need privacy/document training", "I need a licensed partner"]],
  it_research: [["Website or booking project", "Data, automation, or analytics", "Research or portfolio project", "No digital project experience yet"], ["I need tool training", "I can show a portfolio example", "I need security/privacy guidance", "I need a technical mentor"]],
  transportation: [["Local delivery or route coordination", "Customer pickups or ride support", "Vendor, inventory, or dispatch support", "No logistics experience yet"], ["I need vehicle/commercial-insurance guidance", "I need local safety/process training", "I can start with coordination, not driving", "I need a logistics mentor"]],
};

const buildDomainQuestions = (sectorKey) => (SECTOR_DOMAIN_PROMPTS[sectorKey] || []).map((text, index) => ({
  id: `${sectorKey}_domain_${index + 1}`,
  eyebrow: "Domain evidence",
  text,
  options: (SECTOR_DOMAIN_OPTIONS[sectorKey]?.[index] || ["I have done this before", "I have related transferable experience", "I want to learn this first", "I need UC guidance"]).map((label, optionIndex) => ({
    label,
    score: optionIndex === 0 ? { capability_evidence: 3 } : optionIndex === 1 ? { capability_evidence: 2 } : optionIndex === 2 ? { learning_first: 2 } : { mentor_support: 2 },
    reason: optionIndex === 0 ? "You described a directly relevant example." : optionIndex === 1 ? "You described a related capability." : optionIndex === 2 ? "You prefer learning before offering this pathway." : "You need validation before choosing this pathway.",
  })),
}));

const SECTOR_FOLLOWUP_QUESTIONS = {
  healthcare: [
    {
      id: "healthcare_focus",
      eyebrow: "Healthcare path",
      text: "Which healthcare or wellness support path feels closest?",
      options: [
        { label: "Senior companionship / family support", score: { healthcare: 3, community_support: 1 }, reason: "Senior and family support can become a community wellness path." },
        { label: "Yoga / Ayurveda / naturopathy awareness", score: { healthcare: 3, education: 1, licensed_partner: 1 }, reason: "Wellness education fits healthcare with clear professional boundaries." },
        { label: "Healthy meal-prep support", score: { healthcare: 2, small_industries: 1 }, reason: "Healthy meal-prep can support families and seniors with safe boundaries." },
        { label: "Healthcare appointment/logistics support", score: { healthcare: 2, travel: 1, community_support: 1 }, reason: "Logistics support may help families navigate appointments and care coordination." },
        { label: "Licensed provider partnership", score: { healthcare: 2, licensed_partner: 2, enterprise_connection: 1 }, reason: "Licensed-provider routing needs professional partner review." },
      ],
    },
    {
      id: "healthcare_boundary",
      eyebrow: "Safety boundary",
      text: "What boundary should UC keep clear for this healthcare path?",
      options: [
        { label: "Education only, no medical advice", score: { healthcare: 2, education: 1, low_risk_path: 1 }, reason: "Education-only support is safer for community wellness." },
        { label: "Route medical questions to licensed professionals", score: { healthcare: 2, licensed_partner: 2 }, reason: "Licensed-professional routing is required for medical guidance." },
        { label: "I need training before helping", score: { training_support: 2, mentor_support: 1 }, reason: "Training should come before taking on health-related support." },
        { label: "I can support logistics, not advice", score: { healthcare: 1, travel: 1, community_support: 1 }, reason: "Logistics support can be helpful without crossing advice boundaries." },
      ],
    },
  ],
  education: [
    {
      id: "education_focus",
      eyebrow: "Education path",
      text: "Which education opportunity are you most interested in?",
      options: [
        { label: "Tutoring / homework support", score: { education: 3, student_mentor: 1 }, reason: "Tutoring is a direct education-sector path." },
        { label: "Art / craft classes", score: { education: 3, small_industries: 1 }, reason: "Art classes can become small group workshops." },
        { label: "Music / dance / cultural lessons", score: { education: 3, travel: 1, community_support: 1 }, reason: "Culture and music lessons fit education and community programming." },
        { label: "Shloka / language / festival learning", score: { education: 3, community_support: 1 }, reason: "Cultural learning can become a family-friendly education offering." },
        { label: "Student internship mentoring", score: { education: 2, internship_match: 2, student_mentor: 2 }, reason: "Student mentoring should route toward supervised UC matching." },
      ],
    },
    {
      id: "education_audience",
      eyebrow: "Learners",
      text: "Who would you prefer to help first?",
      options: [
        { label: "Young children", score: { education: 2, youth_safety: 2 }, reason: "Children-focused programs need family-safe supervision." },
        { label: "Middle/high-school students", score: { education: 2, student_mentor: 1, youth_safety: 1 }, reason: "School-age learners may need mentoring and structure." },
        { label: "College students", score: { education: 2, internship_match: 1 }, reason: "College students can benefit from skill and career exposure." },
        { label: "Adults / families", score: { education: 1, community_support: 1 }, reason: "Adult learning can be delivered through community sessions." },
      ],
    },
  ],
  small_industries: [
    {
      id: "small_industry_focus",
      eyebrow: "Small-business path",
      text: "Which small-industry idea feels most practical?",
      options: [
        { label: "Tiffin / catering / meal boxes", score: { small_industries: 3, healthcare: 1 }, reason: "Food service fits a small-industry path with local demand." },
        { label: "Baking / sweets / snacks", score: { small_industries: 3, travel: 1 }, reason: "Baking and snacks can start with small batches and events." },
        { label: "Festival or pooja kits", score: { small_industries: 3, travel: 1, community_support: 1 }, reason: "Festival/pooja kits fit Indo-American community needs." },
        { label: "Handmade products / reselling", score: { small_industries: 2, finance_support: 1 }, reason: "Products and reselling need pricing and inventory readiness." },
        { label: "Family-supported home business", score: { small_industries: 2, family_priority: 1, low_risk_path: 1 }, reason: "A family-supported model can start carefully." },
      ],
    },
    {
      id: "small_industry_readiness",
      eyebrow: "Readiness",
      text: "What do you need most before starting a small business?",
      options: [
        { label: "Pricing and cost planning", score: { finance: 2, finance_support: 2 }, reason: "Pricing and cost planning should come before launch." },
        { label: "Licensing / permit awareness", score: { legal: 2, legal_support: 2 }, reason: "Permits and compliance must be checked for home businesses." },
        { label: "Packaging / branding help", score: { it: 1, small_industries: 2 }, reason: "Packaging and branding can make the business more credible." },
        { label: "First customers through community", score: { community_support: 2, action_ready: 1 }, reason: "Community introductions can help test demand." },
      ],
    },
  ],
  finance: [
    {
      id: "finance_focus",
      eyebrow: "Finance path",
      text: "Which finance support area are you exploring?",
      options: [
        { label: "Family budgeting / settlement planning", score: { finance: 3, family_priority: 1 }, reason: "Family budgeting fits UC’s stability planning." },
        { label: "Small-business cost planning", score: { finance: 3, small_industries: 1, finance_support: 2 }, reason: "Business cost planning can reduce launch risk." },
        { label: "Bookkeeping organization", score: { finance: 2, banking: 1, behind_scenes: 1 }, reason: "Bookkeeping support can be a behind-the-scenes opportunity." },
        { label: "Tax/document readiness education", score: { finance: 2, legal_support: 1, education: 1 }, reason: "Document readiness should stay educational and routed as needed." },
        { label: "Professional finance partner routing", score: { finance: 2, licensed_partner: 1 }, reason: "Professional topics should route to qualified partners." },
      ],
    },
    {
      id: "finance_comfort",
      eyebrow: "Comfort",
      text: "How would you like to support finance-related work?",
      options: [
        { label: "Behind-the-scenes organization", score: { finance: 2, behind_scenes: 2 }, reason: "Behind-the-scenes finance organization may fit your comfort." },
        { label: "Community education sessions", score: { finance: 2, education: 1, community_support: 1 }, reason: "Finance awareness can be taught in community settings." },
        { label: "One-on-one only after UC introduction", score: { mentor_support: 1, finance_support: 1 }, reason: "Warm introductions can make finance discussions safer." },
        { label: "I need training first", score: { training_support: 2, low_risk_path: 1 }, reason: "Training should come before finance support." },
      ],
    },
  ],
  insurance: [
    {
      id: "insurance_focus",
      eyebrow: "Insurance path",
      text: "Which insurance and protection area would you like to understand first?",
      options: [
        { label: "Life insurance and family protection", score: { insurance: 3, family_priority: 1 }, reason: "Life protection can be discussed with a qualified licensed professional." },
        { label: "Health insurance and care costs", score: { insurance: 3, healthcare: 1 }, reason: "Health coverage questions should be routed to qualified professionals." },
        { label: "Kids’ education funding", score: { insurance: 3, education: 1, family_priority: 1 }, reason: "Education funding is a family planning topic, not a business-capability signal." },
        { label: "Retirement income and longevity", score: { insurance: 3, finance: 1, family_priority: 1 }, reason: "Retirement planning requires qualified financial and insurance guidance." },
      ],
    },
    {
      id: "insurance_boundary",
      eyebrow: "Boundary",
      text: "What role should UC play in insurance topics?",
      options: [
        { label: "Education only", score: { insurance: 2, education: 1, low_risk_path: 1 }, reason: "Education-only support avoids giving product advice." },
        { label: "Route to licensed agents", score: { insurance: 2, licensed_partner: 2 }, reason: "Insurance product decisions should go to licensed professionals." },
        { label: "Help families prepare questions", score: { insurance: 2, family_priority: 1 }, reason: "Question-preparation support can be useful and safe." },
        { label: "I need UC guidance first", score: { mentor_support: 1, insurance_support: 1 }, reason: "Guidance can help define the right next step." },
      ],
    },
  ],
  travel: [
    {
      id: "travel_focus",
      eyebrow: "Travel/events path",
      text: "Which coordination opportunity fits you best?",
      options: [
        { label: "Temple / pooja / cultural event coordination", score: { travel: 3, community_support: 1 }, reason: "Cultural coordination fits UC’s travel/events sector." },
        { label: "Senior ride or newcomer support", score: { travel: 2, healthcare: 1, community_support: 2 }, reason: "Ride/newcomer support can help families and seniors." },
        { label: "Group travel or community outings", score: { travel: 3, action_ready: 1 }, reason: "Group outings need planning and people coordination." },
        { label: "Event logistics / vendor coordination", score: { travel: 2, small_industries: 1, enterprise_connection: 1 }, reason: "Event logistics can connect community and business partners." },
      ],
    },
    {
      id: "travel_style",
      eyebrow: "Coordination style",
      text: "What part of coordination do you enjoy most?",
      options: [
        { label: "Talking to people and confirming details", score: { travel: 2, community_support: 1 }, reason: "People-facing coordination may fit you." },
        { label: "Planning schedules and checklists", score: { travel: 2, behind_scenes: 1 }, reason: "Planning work can be done before the event." },
        { label: "Helping onsite during events", score: { travel: 2, action_ready: 1 }, reason: "Onsite support fits event execution." },
        { label: "I need a UC team around me", score: { mentor_support: 1, community_support: 1 }, reason: "Team-based coordination may make starting easier." },
      ],
    },
  ],
  real_estate: [
    {
      id: "real_estate_focus",
      eyebrow: "Real-estate path",
      text: "Which real-estate support path are you exploring?",
      options: [
        { label: "Rental or home-search orientation", score: { real_estate: 3, community_support: 1 }, reason: "Rental/home-search orientation fits newcomer and relocation support." },
        { label: "Showing or open-house coordination support", score: { real_estate: 3, action_ready: 1 }, reason: "Showing coordination is a practical people-facing real-estate support path." },
        { label: "Relocation / newcomer settling guidance", score: { real_estate: 2, banking: 1, community_support: 2 }, reason: "Relocation guidance can connect housing, banking, schools, and community support." },
        { label: "Property document checklist support", score: { real_estate: 2, legal_support: 1, behind_scenes: 1 }, reason: "Document checklist support should stay administrative and boundary-aware." },
        { label: "Market research / investor lead support", score: { real_estate: 2, research: 2, finance_support: 1 }, reason: "Market research can support investors without giving financial advice." },
      ],
    },
    {
      id: "real_estate_boundary",
      eyebrow: "Professional boundary",
      text: "Which boundary should UC maintain for real-estate support?",
      options: [
        { label: "Licensed agent handles advice and transactions", score: { real_estate: 2, licensed_partner: 2 }, reason: "Real-estate advice and transactions should route to licensed professionals." },
        { label: "I can coordinate, not advise", score: { real_estate: 2, low_risk_path: 1 }, reason: "Coordination can be useful when advice boundaries are clear." },
        { label: "I need training on what is allowed", score: { real_estate: 1, training_support: 2, legal_support: 1 }, reason: "Training should clarify real-estate compliance boundaries." },
        { label: "UC should introduce me to a broker/mentor", score: { real_estate: 2, mentor_support: 2, enterprise_connection: 1 }, reason: "Broker or mentor introductions can keep the path safer." },
      ],
    },
    {
      id: "real_estate_audience",
      eyebrow: "Audience",
      text: "Who would you most naturally help in real estate?",
      options: [
        { label: "New immigrant families", score: { real_estate: 2, community_support: 2, family_priority: 1 }, reason: "New families often need relocation and housing orientation." },
        { label: "Students or renters", score: { real_estate: 2, education: 1, community_support: 1 }, reason: "Students and renters may need practical rental orientation." },
        { label: "Seniors downsizing or relocating", score: { real_estate: 2, healthcare: 1, family_priority: 1 }, reason: "Seniors may need patient, family-aware coordination." },
        { label: "Property owners needing admin help", score: { real_estate: 2, behind_scenes: 1 }, reason: "Owner support can involve scheduling, documentation, and follow-up." },
        { label: "Investors needing research support", score: { real_estate: 2, research: 2, finance_support: 1 }, reason: "Investor support should focus on research, not financial advice." },
      ],
    },
  ],
  it_research: [
    {
      id: "it_research_focus",
      eyebrow: "IT / research path",
      text: "Which digital or research opportunity feels closest?",
      options: [
        { label: "Website or booking-form setup", score: { it: 3, small_industries: 1 }, reason: "Website and booking support can help community businesses." },
        { label: "Data entry / automation support", score: { it: 2, research: 1, behind_scenes: 2 }, reason: "Data and automation are good behind-the-scenes paths." },
        { label: "Market or community research", score: { research: 3, finance_support: 1 }, reason: "Research can help UC and entrepreneurs understand demand." },
        { label: "Online class or content setup", score: { it: 2, education: 1 }, reason: "Online setup can support teachers, artists, wellness educators, and priests." },
        { label: "AI / analytics project support", score: { it: 3, research: 2, internship_match: 1 }, reason: "AI and analytics can connect to student and enterprise projects." },
      ],
    },
    {
      id: "it_research_style",
      eyebrow: "Work style",
      text: "How do you prefer to contribute digitally?",
      options: [
        { label: "Quiet behind-the-scenes tasks", score: { behind_scenes: 3, research: 1 }, reason: "Behind-the-scenes digital work fits your comfort style." },
        { label: "Client-facing setup help", score: { it: 2, enterprise_connection: 1 }, reason: "Client setup work can support UC partners." },
        { label: "Student/project mentoring", score: { it: 2, student_mentor: 1, internship_match: 1 }, reason: "Digital projects can become supervised student opportunities." },
        { label: "I need training first", score: { training_support: 2, low_risk_path: 1 }, reason: "Training can help choose a safer first digital task." },
      ],
    },
  ],
};

const SECTOR_DOMAIN_QUESTIONS = Object.fromEntries(
  Object.keys(SECTOR_DOMAIN_PROMPTS).map((key) => [key, buildDomainQuestions(key)])
);

const V2_SECTOR_PRIORITY_QUESTION = {
  id: "v2_sector_priority",
  eyebrow: "Choose a focus area",
  text: "Which selected opportunity area should UC assess in more detail first?",
  options: Object.entries(V2_SECTOR_EXPLORATION).map(([label, mapping]) => ({
    label,
    score: { [mapping.scoreKey]: 4 },
    reason: `You chose ${label} as the area to validate first.`,
  })),
};

const getV2SelectedAreas = (answers = {}) =>
  (Array.isArray(answers.v2_sector_exploration) ? answers.v2_sector_exploration : [])
    .map((label) => ({ label, mapping: V2_SECTOR_EXPLORATION[label] }))
    .filter((item) => item.mapping);

const buildV2Flow = (answers = {}) => {
  const selectedAreas = getV2SelectedAreas(answers);
  if (!selectedAreas.length) return V2_QUESTIONS;

  const explorationIndex = V2_QUESTIONS.findIndex((question) => question.id === "v2_sector_exploration");
  const beforeExploration = V2_QUESTIONS.slice(0, explorationIndex + 1);
  const afterExploration = V2_QUESTIONS.slice(explorationIndex + 1);
  const selectedPriority = selectedAreas.find((item) => item.label === answers.v2_sector_priority);
  const primaryArea = selectedPriority || (selectedAreas.length === 1 ? selectedAreas[0] : null);
  const priorityQuestion = selectedAreas.length > 1
    ? {
        ...V2_SECTOR_PRIORITY_QUESTION,
        options: V2_SECTOR_PRIORITY_QUESTION.options.filter((option) =>
          selectedAreas.some((item) => item.label === option.label)
        ),
      }
    : null;

  return [
    ...beforeExploration,
    ...(priorityQuestion ? [priorityQuestion] : []),
    ...(primaryArea ? (SECTOR_DOMAIN_QUESTIONS[primaryArea.mapping.flowKey] || []) : []),
    ...afterExploration,
  ];
};

const STUDENT_QUESTIONS = [
  {
    id: "student_stage",
    eyebrow: "Student stage",
    text: "Who are we helping with internship or mentoring support?",
    options: [
      { label: "High-school student", score: { education: 2, youth_safety: 3, student_mentor: 2 }, reason: "A younger student path should stay supervised and family-aware." },
      { label: "College student", score: { education: 2, it: 1, internship_match: 2, student_mentor: 1 }, reason: "College students may benefit from internships, projects, and mentors." },
      { label: "Recent graduate", score: { it: 1, research: 1, internship_match: 2, enterprise_connection: 1 }, reason: "A recent graduate may need project exposure and warm introductions." },
      { label: "Parent looking for guidance", score: { education: 2, family_priority: 1, youth_safety: 2 }, reason: "Family context should shape the student guidance path." },
      { label: "School/community leader", score: { education: 2, enterprise_connection: 2, student_mentor: 2 }, reason: "Leaders can help UC build a supervised student opportunity network." },
    ],
  },
  {
    id: "student_need",
    eyebrow: "Support need",
    text: "What would help most right now?",
    options: [
      { label: "Find an internship", score: { internship_match: 3, enterprise_connection: 1, it: 1 }, reason: "You are looking for internship matching." },
      { label: "Find a mentor", score: { student_mentor: 3, mentor_support: 2, education: 1 }, reason: "Mentor matching may be the best first step." },
      { label: "Build a project portfolio", score: { it: 2, research: 2, training_support: 1 }, reason: "Project work can make a student more visible to employers." },
      { label: "Resume/interview guidance", score: { education: 2, training_support: 2 }, reason: "Career readiness coaching may help quickly." },
      { label: "Career exposure sessions", score: { education: 2, community_support: 1 }, reason: "Exposure sessions can help students choose a direction." },
    ],
  },
  {
    id: "student_track",
    eyebrow: "Track",
    text: "Which track feels closest to the student’s interest?",
    options: [
      { label: "Technology / data / AI", score: { it: 3, research: 1, internship_match: 1 }, reason: "Technology and data can create project and internship paths." },
      { label: "Healthcare / wellness", score: { healthcare: 2, education: 1, internship_match: 1 }, reason: "Healthcare interest can route toward supervised exposure or education." },
      { label: "Finance / banking", score: { finance: 2, banking: 2, education: 1 }, reason: "Finance interest can start with literacy, analysis, or operations exposure." },
      { label: "Law / immigration policy", score: { legal: 2, research: 2, immigration_routing: 1 }, reason: "Legal/immigration interests should be routed with careful boundaries." },
      { label: "Teaching / community service", score: { education: 3, community_support: 1 }, reason: "Teaching and service fit UC’s education/community sector." },
      { label: "Business / operations", score: { small_industries: 2, finance: 1, enterprise_connection: 1 }, reason: "Business interests can connect to UC enterprise and operations paths." },
    ],
  },
  TIME_QUESTION,
  {
    id: "student_safety",
    eyebrow: "Safety",
    text: "For student matching, what supervision level is needed?",
    options: [
      { label: "Parent/guardian should be involved", score: { youth_safety: 3, family_priority: 1 }, reason: "UC should keep family-aware supervision in the process." },
      { label: "School/organization supervised", score: { youth_safety: 3, enterprise_connection: 1 }, reason: "A school or organization channel can make matching safer." },
      { label: "Adult student can coordinate directly", score: { internship_match: 1, student_mentor: 1 }, reason: "Adult students may be able to coordinate directly after UC screening." },
      { label: "Not sure yet", score: { youth_safety: 2, mentor_support: 1 }, reason: "UC can help decide the safest matching process." },
    ],
  },
  {
    id: "student_next_step",
    eyebrow: "Next step",
    text: "What should UC help prepare next?",
    options: [
      { label: "A supervised mentor introduction", score: { student_mentor: 3, youth_safety: 2 }, reason: "A supervised introduction is the safest first connection." },
      { label: "A project or internship application plan", score: { internship_match: 3, training_support: 1 }, reason: "A structured application plan can turn interest into evidence." },
      { label: "A portfolio or resume review", score: { education: 2, training_support: 2 }, reason: "A review can clarify gaps before matching." },
      { label: "A General Advisor discussion", score: { mentor_support: 3 }, reason: "A General Advisor can help choose a responsible next step." },
    ],
  },
];

const IMMIGRATION_QUESTIONS = [
  {
    id: "immigration_need",
    eyebrow: "Immigration topic",
    text: "What kind of immigration help are you exploring?",
    options: [
      { label: "O-1 extraordinary ability", score: { legal: 3, research: 1, immigration_routing: 3 }, reason: "You may need attorney guidance for an O-1 conversation." },
      { label: "NIW green card", score: { legal: 3, research: 2, immigration_routing: 3 }, reason: "You may need attorney guidance for an NIW conversation." },
      { label: "EB-1A green card", score: { legal: 3, research: 2, immigration_routing: 3 }, reason: "You may need attorney guidance for an EB-1A conversation." },
      { label: "Not sure which option", score: { legal: 2, mentor_support: 1, immigration_routing: 2 }, reason: "You want high-level routing before speaking with an attorney." },
      { label: "Employer/startup immigration question", score: { legal: 2, finance: 1, enterprise_connection: 1, immigration_routing: 2 }, reason: "Business/employer context may require attorney routing." },
    ],
  },
  {
    id: "immigration_background",
    eyebrow: "Background",
    text: "Which background best describes you? Please keep this non-confidential.",
    options: [
      { label: "Founder / entrepreneur", score: { small_industries: 1, finance: 1, legal: 2, enterprise_connection: 1 }, reason: "Founder context may connect immigration, business, and finance planning." },
      { label: "Researcher / scientist", score: { research: 3, legal: 1, immigration_routing: 1 }, reason: "Research background may need evidence organization before attorney review." },
      { label: "Tech professional", score: { it: 2, research: 1, legal: 1 }, reason: "Tech professionals may need achievement and project documentation." },
      { label: "Healthcare professional", score: { healthcare: 2, legal: 1, immigration_routing: 1 }, reason: "Healthcare background may need credential-sensitive attorney review." },
      { label: "Artist / musician / cultural professional", score: { education: 2, travel: 1, legal: 1 }, reason: "Creative achievements may need portfolio-style organization." },
      { label: "Student / early-career", score: { education: 2, student_mentor: 1, legal: 1 }, reason: "Early-career users may need mentoring and expectation-setting first." },
    ],
  },
  {
    id: "immigration_readiness",
    eyebrow: "Preparation",
    text: "What would help before a lawyer conversation?",
    options: [
      { label: "Organize achievements/evidence", score: { research: 2, legal: 1, immigration_routing: 1 }, reason: "Evidence organization can make the attorney conversation more productive." },
      { label: "Understand options at a high level", score: { education: 1, legal: 2, mentor_support: 1 }, reason: "High-level education may help before legal advice." },
      { label: "Find the right attorney", score: { immigration_routing: 3, legal: 2 }, reason: "UC should route you to a qualified immigration attorney." },
      { label: "Plan cost and timeline", score: { finance: 2, legal: 1, finance_support: 2 }, reason: "Financial planning may be part of the next step." },
      { label: "Employer/business coordination", score: { enterprise_connection: 2, legal: 1, finance: 1 }, reason: "Business context can affect the support network around you." },
    ],
  },
  {
    id: "immigration_confidentiality",
    eyebrow: "Privacy boundary",
    text: "What are you comfortable sharing in UC Companion?",
    options: [
      { label: "Only non-confidential professional summary", score: { legal: 1, immigration_routing: 2 }, reason: "Keeping the intake non-confidential protects the visitor and UC." },
      { label: "I only want attorney contact info", score: { immigration_routing: 3, legal: 1 }, reason: "A direct attorney-routing path is appropriate." },
      { label: "I need help knowing what not to share", score: { legal: 2, mentor_support: 1 }, reason: "UC should show privacy boundaries before collecting details." },
      { label: "I prefer a phone call after sign-in", score: { community_support: 1, immigration_routing: 2 }, reason: "A guided signed-in request may fit you better." },
    ],
  },
  {
    id: "immigration_next_action",
    eyebrow: "Next action",
    text: "What would you like UC to help you do next?",
    options: [
      { label: "Request a screened immigration-attorney introduction", score: { immigration_routing: 3, legal: 2 }, reason: "UC can route a non-confidential summary to a qualified attorney." },
      { label: "Prepare a non-confidential evidence checklist", score: { immigration_routing: 2, research: 2 }, reason: "An evidence checklist can make a professional consultation more focused." },
      { label: "Attend an educational UC session first", score: { education: 2, community_support: 2 }, reason: "Education can come before any individualized legal discussion." },
      { label: "Speak with a UC General Advisor", score: { mentor_support: 3, immigration_routing: 1 }, reason: "A General Advisor can help organize your next step without giving legal advice." },
    ],
  },
];

const MENTOR_PROVIDER_QUESTIONS = [
  {
    id: "mentor_support_style",
    eyebrow: "Supporting talent",
    text: "How would you like to support emerging talent?",
    options: [
      { label: "Volunteer as a mentor", score: { student_mentor: 2, community_support: 1 }, reason: "You are exploring volunteer mentoring." },
      { label: "Offer career guidance", score: { student_mentor: 2, education: 1 }, reason: "You want to offer career guidance." },
      { label: "Provide short-term projects", score: { internship_match: 2, enterprise_connection: 1 }, reason: "You may be able to offer practical project experience." },
      { label: "Host interns through my organization", score: { internship_match: 3, enterprise_connection: 2 }, reason: "You are exploring an internship-host pathway." },
      { label: "Offer paid coaching or training", score: { education: 1 }, reason: "You are exploring a paid coaching or training service." },
      { label: "Speak at UC events", score: { community_support: 2, education: 1 }, reason: "You may contribute as a UC guest speaker." },
      { label: "Help with resumes or interviews", score: { student_mentor: 2, education: 1 }, reason: "You are interested in career-readiness support." },
      { label: "I am still exploring", score: { mentor_support: 1 }, reason: "You are still exploring a contribution pathway." },
    ],
  },
  {
    id: "mentor_primary_goal",
    eyebrow: "Primary goal",
    text: "What is your primary goal?",
    options: [
      { label: "Community contribution", score: { community_support: 2 }, reason: "You want to make a community contribution." },
      { label: "Professional networking", score: { enterprise_connection: 1 }, reason: "You are seeking professional networking through UC." },
      { label: "Build a talent pipeline", score: { internship_match: 2, enterprise_connection: 2 }, reason: "You want to build an emerging-talent pipeline." },
      { label: "Promote my organization", score: { enterprise_connection: 1 }, reason: "You would like to connect your organization with UC." },
      { label: "Offer a paid service", score: {}, reason: "You are considering a paid service." },
      { label: "Explore supplemental income", score: {}, reason: "You are exploring supplemental income." },
      { label: "Not sure yet", score: { mentor_support: 1 }, reason: "You are still deciding on the right pathway." },
    ],
  },
  {
    id: "mentor_experience_area",
    eyebrow: "Experience area",
    text: "Which professional or life experience could you share with UC members?",
    options: [
      { label: "Leadership, management, or hiring", score: { student_mentor: 1 }, reason: "You can share leadership or career-development experience." },
      { label: "Technical, research, or operations work", score: { student_mentor: 1 }, reason: "You can share practical professional experience." },
      { label: "Teaching, training, or public speaking", score: { education: 1 }, reason: "You may have a strong mentoring or learning-facilitation foundation." },
      { label: "Small business or entrepreneurship", score: { enterprise_connection: 1 }, reason: "You can share enterprise and business-building experience." },
      { label: "Community, family, or life experience", score: { community_support: 1 }, reason: "Life experience can be valuable for a community-support role." },
      { label: "I need help identifying my experience area", score: { mentor_support: 1 }, reason: "UC can help you identify a meaningful contribution." },
    ],
  },
  TIME_QUESTION,
  {
    id: "mentor_audience",
    eyebrow: "Preferred audience",
    text: "Who would you most like to support first?",
    options: [
      { label: "High-school students", score: { youth_safety: 2, student_mentor: 1 }, reason: "High-school support requires clear supervision." },
      { label: "College students", score: { internship_match: 1, student_mentor: 1 }, reason: "College students may benefit from career exposure and projects." },
      { label: "Recent graduates", score: { student_mentor: 1 }, reason: "Recent graduates may benefit from career guidance." },
      { label: "Newcomers or career changers", score: { community_support: 1 }, reason: "Newcomers and career changers may benefit from practical guidance." },
      { label: "Small-business owners", score: { enterprise_connection: 1 }, reason: "Small-business owners may benefit from experienced guidance." },
    ],
  },
  {
    id: "mentor_format",
    eyebrow: "Mentoring format",
    text: "Which mentoring or contribution format suits you?",
    options: [
      { label: "One-on-one mentoring", score: { student_mentor: 1 }, reason: "You prefer a focused one-on-one format." },
      { label: "Small group session", score: { community_support: 1 }, reason: "You prefer a small-group format." },
      { label: "Workshop or guest talk", score: { education: 1, community_support: 1 }, reason: "You prefer sharing knowledge with a group." },
      { label: "Short supervised project", score: { internship_match: 1 }, reason: "You prefer a practical project format." },
      { label: "Resume or interview clinic", score: { student_mentor: 1 }, reason: "You prefer a career-readiness format." },
    ],
  },
  {
    id: "mentor_availability",
    eyebrow: "Availability",
    text: "What availability can you realistically offer?",
    options: [
      { label: "One hour a month", score: { community_support: 1 }, reason: "A light-touch contribution can still help." },
      { label: "One to two hours a month", score: { student_mentor: 1 }, reason: "You can support a periodic UC engagement." },
      { label: "Two to four hours a month", score: { student_mentor: 1 }, reason: "You may be able to support mentoring or a small session." },
      { label: "A defined short-term project", score: { internship_match: 1 }, reason: "A defined project can give a learner practical exposure." },
    ],
  },
  {
    id: "mentor_example",
    eyebrow: "Example",
    text: "Which example best reflects how you have guided, trained, supervised, or supported others?",
    options: [
      { label: "Led or supervised people at work", score: { student_mentor: 2 }, reason: "You have leadership or supervision experience." },
      { label: "Taught, trained, or coached someone", score: { education: 2, student_mentor: 2 }, reason: "You have direct teaching or coaching experience." },
      { label: "Reviewed resumes, projects, or interviews", score: { student_mentor: 2 }, reason: "You have career-readiness support experience." },
      { label: "Organized a community or family activity", score: { community_support: 2 }, reason: "You have community-support experience." },
      { label: "I need help identifying an example", score: { mentor_support: 1 }, reason: "UC can help you translate your experience into a safe mentoring role." },
    ],
  },
  {
    id: "mentor_compensation",
    eyebrow: "Volunteer or paid",
    text: "Would you prefer to contribute as a volunteer or offer a paid service?",
    options: [
      { label: "Volunteer contribution", score: { community_support: 2 }, reason: "You are offering a volunteer contribution." },
      { label: "Open to volunteer or paid", score: { community_support: 1 }, reason: "You are open to discussing the right structure." },
      { label: "Paid coaching or training", score: {}, reason: "You want to explore a paid coaching or training service." },
      { label: "Not sure yet", score: { mentor_support: 1 }, reason: "UC can help clarify the right contribution model." },
    ],
  },
];

const LAW_FIRM_QUESTIONS = [
  {
    id: "law_firm_role",
    eyebrow: "Role",
    text: "Which best describes your role?",
    options: [
      { label: "Licensed attorney", score: { legal: 3, licensed_partner: 3, law_firm_partner: 2 }, reason: "Licensed attorneys can be reviewed for UC partner routing." },
      { label: "Law-firm representative", score: { legal: 2, law_firm_partner: 3, licensed_partner: 1 }, reason: "A firm representative can start the partner intake." },
      { label: "Immigration consultant / document support", score: { legal: 1, law_firm_partner: 1 }, reason: "UC should review non-attorney boundaries carefully." },
      { label: "Business service provider", score: { small_industries: 1, legal_support: 1, enterprise_connection: 1 }, reason: "Business services may fit UC but not legal-advice routing." },
    ],
  },
  {
    id: "law_practice_area",
    eyebrow: "Practice area",
    text: "Which service area would you support?",
    options: [
      { label: "O-1 / NIW / EB-1A", score: { legal: 3, immigration_routing: 3, law_firm_partner: 1 }, reason: "Employment-based immigration routing is a strong UC use case." },
      { label: "Startup / business immigration", score: { legal: 2, small_industries: 1, enterprise_connection: 1 }, reason: "Startup immigration can connect with UC entrepreneurship goals." },
      { label: "Business formation / contracts", score: { legal: 2, small_industries: 2, legal_support: 1 }, reason: "Business setup support can help UC entrepreneurs." },
      { label: "Employment / HR compliance", score: { legal: 2, enterprise_connection: 2 }, reason: "Employment compliance can support UC partners." },
      { label: "Community legal education", score: { legal: 2, education: 2, community_support: 1 }, reason: "Legal education can help the community without giving individual advice." },
    ],
  },
  {
    id: "law_partner_model",
    eyebrow: "Partner model",
    text: "What partnership model feels right?",
    options: [
      { label: "Paid consultations with clear fee disclosure", score: { law_firm_partner: 2, licensed_partner: 1, finance_support: 1 }, reason: "Transparent fee disclosure matters for community trust." },
      { label: "Free first educational webinar", score: { education: 2, community_support: 2, law_firm_partner: 1 }, reason: "Educational webinars can build trust at scale." },
      { label: "Limited pro-bono/community slots", score: { community_support: 2, legal: 1, law_firm_partner: 1 }, reason: "Community slots can support nonprofit impact." },
      { label: "Screened referral/directory listing", score: { law_firm_partner: 3, licensed_partner: 1 }, reason: "A screened directory needs ethics and compliance review." },
    ],
  },
  {
    id: "law_ethics",
    eyebrow: "Ethics boundary",
    text: "Can your firm support UC’s no-legal-advice-until-attorney-review boundary?",
    options: [
      { label: "Yes, attorney review before advice", score: { licensed_partner: 3, law_firm_partner: 2 }, reason: "Attorney-led review keeps the relationship safer." },
      { label: "Yes, only general education before intake", score: { education: 2, law_firm_partner: 1 }, reason: "General education is safer than individualized advice." },
      { label: "Need to discuss ethics/compliance first", score: { legal: 2, law_firm_partner: 1 }, reason: "Ethics review should happen before partnership." },
      { label: "Not sure", score: { legal_support: 1 }, reason: "UC should pause legal routing until boundaries are clear." },
    ],
  },
  {
    id: "law_partner_capacity",
    eyebrow: "Partner capacity",
    text: "What can your firm responsibly offer UC in the next 90 days?",
    options: [
      { label: "One educational webinar", score: { education: 2, community_support: 2, law_firm_partner: 1 }, reason: "A defined educational session is a low-risk first step." },
      { label: "Screened consultation slots", score: { law_firm_partner: 3, licensed_partner: 2 }, reason: "Screened slots can be reviewed for ethics and fee transparency." },
      { label: "A referral-directory profile", score: { law_firm_partner: 2, licensed_partner: 1 }, reason: "Directory listing requires jurisdiction, credentials, and ethics review." },
      { label: "I need a UC partnership discussion first", score: { mentor_support: 2, law_firm_partner: 1 }, reason: "A partnership discussion can define a safe, transparent model." },
    ],
  },
];

const FAMILY_PRIORITY_QUESTION = {
  id: "family_priority_area",
  eyebrow: "Family priority",
  text: "Which life area needs the most attention right now?",
  options: [
    { label: "Finance settlement / budgeting", score: { finance: 3, banking: 1, finance_support: 2 }, reason: "Finance readiness is a central family priority." },
    { label: "Insurance protection", score: { insurance: 3, insurance_support: 2 }, reason: "Insurance awareness can protect family stability." },
    { label: "Kids education", score: { education: 3, student_mentor: 1 }, reason: "Education support can help children and family planning." },
    { label: "Healthcare / wellness", score: { healthcare: 3, education: 1 }, reason: "Healthcare and wellness guidance may support family resilience." },
    { label: "Second income for family stability", score: { small_industries: 2, finance: 1, action_ready: 1 }, reason: "Second income can support long-term family stability." },
    { label: "Immigration/legal routing", score: { legal: 2, immigration_routing: 2 }, reason: "Legal and immigration questions should be routed to qualified professionals." },
  ],
};

const FAMILY_GENERAL_QUESTIONS = [
  {
    id: "family_support_format",
    eyebrow: "Support route",
    text: "What would be most useful as your next family-support step?",
    options: [
      { label: "Prepare questions for a qualified professional", score: { mentor_support: 2 }, reason: "Question preparation helps families have a focused professional conversation." },
      { label: "Find a UC community meetup", score: { community_support: 2 }, reason: "A meetup can provide education and trusted routing." },
      { label: "Get a screened partner introduction", score: { licensed_partner: 2, enterprise_connection: 1 }, reason: "UC can route regulated topics to screened professionals." },
      { label: "I need a General Advisor first", score: { mentor_support: 3 }, reason: "A General Advisor can help organize multiple family priorities." },
    ],
  },
];

const buildFamilyGuidanceFlow = (answers = {}) => {
  const priority = answers.family_priority_area || "";
  let contextual = [];
  if (priority === "Insurance protection") contextual = SECTOR_FOLLOWUP_QUESTIONS.insurance || [];
  else if (priority === "Finance settlement / budgeting") contextual = SECTOR_FOLLOWUP_QUESTIONS.finance || [];
  else if (priority === "Kids education") contextual = SECTOR_FOLLOWUP_QUESTIONS.education || [];
  else if (priority === "Healthcare / wellness") contextual = SECTOR_FOLLOWUP_QUESTIONS.healthcare || [];
  else if (priority === "Immigration/legal routing") contextual = SECTOR_FOLLOWUP_QUESTIONS.legal || [];
  return [FAMILY_PRIORITY_QUESTION, ...contextual, ...FAMILY_GENERAL_QUESTIONS, TIME_QUESTION].filter(Boolean);
};

const FAMILY_GUIDANCE_QUESTIONS = [FAMILY_PRIORITY_QUESTION, ...FAMILY_GENERAL_QUESTIONS, TIME_QUESTION];

const COMMUNITY_MEETUP_QUESTIONS = [
  {
    id: "meetup_goal",
    eyebrow: "Meetup goal",
    text: "What would you like to discuss at a UC community meetup?",
    options: [
      { label: "Second-income or small-business idea", score: { small_industries: 2, finance_support: 1, mentor_support: 1 }, reason: "A meetup can help you identify a practical first income step." },
      { label: "Student internship or mentor", score: { education: 2, internship_match: 2, student_mentor: 2 }, reason: "A meetup can introduce supervised student mentoring and internship paths." },
      { label: "Immigration help: O-1 / NIW / EB-1A", score: { legal: 2, immigration_routing: 3 }, reason: "A meetup can explain UC's attorney-routing boundary before legal intake." },
      { label: "Family finance, insurance, or education", score: { finance: 1, insurance: 1, education: 1, family_priority: 2 }, reason: "Family planning can be routed to the right UC sector leads." },
      { label: "Healthcare / Ayurveda / wellness", score: { healthcare: 2, education: 1, licensed_partner: 1 }, reason: "Wellness topics can be handled as education and routed to qualified partners where needed." },
      { label: "Priest / ritual services", score: { travel: 1, education: 1, community_support: 2, small_industries: 1 }, reason: "Ritual-service questions can connect with UC's Divine Rituals pathway." },
      { label: "Volunteer, mentor, or become a partner", score: { community_support: 3, enterprise_connection: 1, mentor_support: 1 }, reason: "The meetup can convert community leaders into UC helpers and partners." },
    ],
  },
  {
    id: "meetup_preference",
    eyebrow: "Session preference",
    text: "Which meeting style feels easiest for you?",
    options: [
      { label: "Walk into a local library/city-center session", score: { community_support: 3, travel: 1 }, reason: "A public local session may be the most comfortable entry point." },
      { label: "Reserve a seat before I come", score: { action_ready: 1, community_support: 2 }, reason: "A reserved seat helps UC prepare for your interest area." },
      { label: "Attend online first", score: { it: 1, low_risk_path: 2 }, reason: "An online orientation can reduce pressure before an in-person visit." },
      { label: "Bring my family/student with me", score: { family_priority: 2, youth_safety: 1, education: 1 }, reason: "Family-aware sessions can help UC guide students and households safely." },
      { label: "I need UC to recommend the right session", score: { mentor_support: 2, low_risk_path: 1 }, reason: "A guided recommendation may help you choose the right meetup." },
    ],
  },
  {
    id: "meetup_discussion",
    eyebrow: "Discussion goal",
    text: "What would you like to leave the meetup with?",
    options: [
      { label: "One practical opportunity next step", score: { action_ready: 2, mentor_support: 1 }, reason: "The meetup can help narrow your next action." },
      { label: "A qualified professional referral", score: { licensed_partner: 2, enterprise_connection: 1 }, reason: "UC can explain referral boundaries and next steps." },
      { label: "A mentor, provider, or partner connection", score: { mentor_support: 2, enterprise_connection: 2 }, reason: "Meetups can introduce community contributors safely." },
      { label: "General information only", score: { education: 2, community_support: 1 }, reason: "A general orientation is a valid first step." },
    ],
  },
];

const QUESTIONS_BY_INTENT = {
  second_income: BASE_QUESTIONS,
  student_internship: STUDENT_QUESTIONS,
  immigration_help: IMMIGRATION_QUESTIONS,
  mentor_provider: MENTOR_PROVIDER_QUESTIONS,
  community_meetup: COMMUNITY_MEETUP_QUESTIONS,
  law_firm_partner: LAW_FIRM_QUESTIONS,
  family_guidance: FAMILY_GUIDANCE_QUESTIONS,
  unsure: BASE_QUESTIONS,
};

const SECTOR_FOLLOWUP_BANK = Object.values(SECTOR_FOLLOWUP_QUESTIONS).flat();

const QUESTION_BANK = [
  INTENT_QUESTION,
  ...V2_QUESTIONS,
  V2_SECTOR_PRIORITY_QUESTION,
  ...BASE_QUESTIONS,
  ...SECTOR_FOLLOWUP_BANK,
  ...Object.values(SECTOR_DOMAIN_QUESTIONS).flat(),
  ...STUDENT_QUESTIONS,
  ...IMMIGRATION_QUESTIONS,
  ...MENTOR_PROVIDER_QUESTIONS,
  ...COMMUNITY_MEETUP_QUESTIONS,
  ...LAW_FIRM_QUESTIONS,
  ...FAMILY_GUIDANCE_QUESTIONS,
];

const getIntentById = (intentId) =>
  INTENT_OPTIONS.find((intent) => intent.id === intentId) || null;

const findIntentIdFromLabel = (label) =>
  INTENT_OPTIONS.find((intent) => intent.label === label)?.id || null;

const getQuestionFlow = (intentId, answers = {}) => {
  if (!intentId) return [GATING_QUESTION, ...BASE_QUESTIONS];

  if (["second_income", "mixed", "business_growth", "unsure"].includes(intentId)) {
    return buildV2Flow(answers);
  }

  if (intentId === "family_guidance") return buildFamilyGuidanceFlow(answers);

  return QUESTIONS_BY_INTENT[intentId] || BASE_QUESTIONS;
};

const getQuestionById = (questionId) =>
  QUESTION_BANK.find((question) => question?.id === questionId);

const getIntentResultCopy = (intentId) => {
  switch (intentId) {
    case "student_internship":
      return {
        title: "Student opportunity pathway",
        nextAction: "UC can route this to a supervised mentor, internship, project, or education contact.",
        guardrail:
          "For minors, UC should involve a parent/guardian, school, or supervised organization before any direct matching.",
        contactHeading: "Request student/mentor matching",
        contactPlaceholder:
          "Example: My daughter is in college and needs a summer data/AI internship mentor.",
      };
    case "immigration_help":
      return {
        title: "Immigration attorney routing pathway",
        nextAction: "UC can collect a non-confidential summary and route you to a licensed immigration lawyer.",
        guardrail:
          "The Companion cannot give legal advice, decide eligibility, or collect passports, SSNs, A-numbers, or legal documents.",
        contactHeading: "Request immigration attorney routing",
        contactPlaceholder:
          "Example: I want to speak with an attorney about whether O-1, NIW, or EB-1A may fit my background.",
      };
    case "mentor_provider":
      return {
        title: "Mentor and internship-provider pathway",
        nextAction: "UC can review your offer and match it with students or members using a screened process.",
        guardrail:
          "Student matching should use clear supervision, consent, and safe communication boundaries.",
        contactHeading: "Offer mentoring/internship support",
        contactPlaceholder:
          "Example: I can host two supervised weekend project sessions for college students.",
      };
    case "community_meetup":
      return {
        title: "Community meetup pathway",
        nextAction: "UC can help you choose a local library, city-center, or online session and prepare the right discussion.",
        guardrail:
          "Public meetups are for education, routing, and community introductions; legal, finance, healthcare, and insurance topics should be handled by qualified professionals.",
        contactHeading: "Request meetup follow-up",
        contactPlaceholder:
          "Example: I want to attend the Edison meetup and discuss NIW/EB-1A attorney routing plus student internship options.",
      };
    case "law_firm_partner":
      return {
        title: "Law-firm partner pathway",
        nextAction: "UC can review your firm, practice areas, fee transparency, jurisdiction, and ethics boundaries.",
        guardrail:
          "UC should not fee-share, sell legal referrals, or present legal advice without a licensed attorney and ethics review.",
        contactHeading: "Request law-firm partner review",
        contactPlaceholder:
          "Example: Our firm handles NIW/EB-1A and can offer one educational webinar plus transparent consultation fees.",
      };
    case "family_guidance":
      return {
        title: "Family stability pathway",
        nextAction: "UC can connect your family priority with the right sector: finance, insurance, education, health, legal, or income.",
        guardrail:
          "Financial, insurance, healthcare, and legal recommendations should be routed to qualified professionals where needed.",
        contactHeading: "Request family-guidance support",
        contactPlaceholder:
          "Example: I need help planning insurance, kids education, and a small second-income idea.",
      };
    case "business_growth":
      return {
        title: "Existing business pathway",
        nextAction: "UC can help you validate the next business-growth step and connect with the appropriate partner, mentor, or community resource.",
        guardrail:
          "UC provides planning guidance and introductions, not guaranteed revenue, professional advice, or regulatory approval.",
        contactHeading: "Request an existing-business discussion",
        contactPlaceholder:
          "Example: I want to validate a new service, partnership, or growth plan for my business.",
      };
    case "second_income":
    case "mixed":
      return {
        title: "Supplemental-income opportunity pathway",
        nextAction: "UC can help you validate a practical first step and connect you with the right mentor, sector lead, or enterprise partner.",
        guardrail:
          "Income ranges are illustrative only and require local market, licensing, insurance, and safety validation.",
        contactHeading: "Request a UC opportunity discussion",
        contactPlaceholder:
          "Example: I want to validate a small, low-risk second-income idea in my area.",
      };
    default:
      return {
        title: "Opportunity pathway",
        nextAction: "UC can help you choose a small first step and connect with a mentor, sector lead, or enterprise partner.",
        guardrail:
          "The Companion gives planning guidance and routing, not guaranteed income, professional advice, or certification.",
        contactHeading: "Request a UC introduction",
        contactPlaceholder:
          "Example: I would like guidance on starting weekend art classes.",
      };
  }
};

const INTENT_PROMPT_STORAGE_KEY = "ucOpportunityIntentPromptSeenV1";
const EXPERIENCE_SURVEY_STORAGE_KEY = "ucOpportunityExperienceSurveyV1";
const EXPERIENCE_EXIT_PROMPT_SESSION_KEY = "ucOpportunityExitPromptSeenV1";
const REPORT_CLAIM_STORAGE_KEY = "ucOpportunityReportClaimV1";
const COMPANION_INVITE_SESSION_KEY = "ucCompanionInviteSeenV1";
const PROMOTION_LOCK_STORAGE_KEY = "ucActivePromotionV1";
const COMPANION_INVITE_DELAY_MS = 28000;
const COMPANION_INVITE_DURATION_MS = 8000;
const PROMOTION_RETRY_DELAY_MS = 5000;
const EXPERIENCE_EXIT_INTENT_MIN_DWELL_MS = 20000;

const isRegistrationRoute = (pathname = "") =>
  /^\/2\.0\/(?:community-meetup|event)\/[^/]+\/register\/?$/.test(pathname);

const hasSeenIntentPrompt = () => {
  if (typeof window === "undefined") return true;
  return safeStorageGet(window.sessionStorage, INTENT_PROMPT_STORAGE_KEY) === "yes";
};

const rememberIntentPrompt = () => {
  if (typeof window === "undefined") return;
  safeStorageSet(window.sessionStorage, INTENT_PROMPT_STORAGE_KEY, "yes");
};

const hasCompletedExperienceSurvey = () => {
  if (typeof window === "undefined") return true;
  return safeStorageGet(window.sessionStorage, EXPERIENCE_SURVEY_STORAGE_KEY) === "completed";
};

const rememberExperienceSurvey = (answers = {}) => {
  if (typeof window === "undefined") return;
  safeStorageSet(window.sessionStorage, EXPERIENCE_SURVEY_STORAGE_KEY, "completed");
  safeStorageSet(
    window.sessionStorage,
    `${EXPERIENCE_SURVEY_STORAGE_KEY}:answers`,
    JSON.stringify({ ...answers, completedAt: new Date().toISOString() })
  );
};

const HUMAN_CHECK = {
  text: "Quick check: which one is a real-life goal?",
  options: ["Extra income", "Blue banana code", "Random number cloud"],
  answer: "Extra income",
};

const buildEmptyScores = () => ({
  ...Object.keys(SECTORS).reduce((acc, key) => ({ ...acc, [key]: 0 }), {}),
  ...Object.keys(SUPPORT_LABELS).reduce((acc, key) => ({ ...acc, [key]: 0 }), {}),
});

const addScore = (scores, score = {}) => {
  Object.entries(score).forEach(([key, value]) => {
    scores[key] = (scores[key] || 0) + value;
  });
};

const sortByScore = (scores, keys) =>
  keys
    .map((key) => ({ key, score: scores[key] || 0 }))
    .sort((a, b) => b.score - a.score);

const getAnswerOption = (answers, questionId) => {
  const question = getQuestionById(questionId);
  const label = answers[questionId];
  if (Array.isArray(label)) return question?.options.filter((option) => label.includes(option.label));
  return question?.options.find((option) => option.label === label);
};

const buildSnapshot = (primaryKey, supportTags, answers, scores) => {
  const primary = SECTORS[primaryKey];
  const time = getAnswerOption(answers, "time")?.snapshot?.time || "5–10 hrs/week";
  const money =
    getAnswerOption(answers, "investment")?.snapshot?.money ||
    (scores.low_investment >= 3 ? "$0–$300" : "$100–$1,000");

  const supportNeeded = supportTags
    .slice(0, 3)
    .map(({ key }) => SUPPORT_LABELS[key])
    .filter(Boolean);

  if (!supportNeeded.length) {
    supportNeeded.push("UC mentor introduction");
  }

  return {
    time,
    energy: primary.energy,
    money,
    timeline: primary.timeline,
    income: "Revenue potential requires local market validation.",
    risk: primary.risk,
    support: supportNeeded.join(", "),
  };
};

const buildRecommendations = (answers) => {
  const scores = buildEmptyScores();
  const reasons = [];
  const intentId = answers.intentId || findIntentIdFromLabel(answers.intent);
  const intent = getIntentById(intentId);
  const userIntent = intentId || "uncertain";
  const isGuidanceOnly = userIntent === "family_guidance";
  const mentorPathwayReport = intentId === "mentor_provider"
    ? buildMentorPathwayReport(answers)
    : null;

  if (intent?.score) {
    addScore(scores, intent.score);
  }

  Object.entries(answers).forEach(([questionId, selectedLabel]) => {
    if (questionId === "intentId" || (!Array.isArray(selectedLabel) && typeof selectedLabel !== "string")) return;
    const question = getQuestionById(questionId);
    const selectedLabels = Array.isArray(selectedLabel) ? selectedLabel : [selectedLabel];
    selectedLabels.forEach((label) => {
      const selected = question?.options.find((option) => option.label === label);
      if (selected) {
        if (questionId === "intent") {
          if (selected.reason) reasons.push(selected.reason);
          return;
        }
        addScore(scores, selected.score);
        if (selected.reason) reasons.push(selected.reason);
      }
    });
  });

  const sectorKeys = Object.keys(SECTORS);
  const sectorRank = sortByScore(scores, sectorKeys);
  const selectedV2Areas = Array.isArray(answers.v2_sector_exploration)
    ? answers.v2_sector_exploration
    : [];
  const selectedV2Focus = answers.v2_sector_priority ||
    (selectedV2Areas.length === 1 ? selectedV2Areas[0] : null);
  const explicitPrimaryKey = V2_SECTOR_EXPLORATION[selectedV2Focus]?.scoreKey;
  const primaryKey = explicitPrimaryKey || (sectorRank[0]?.score > 0 ? sectorRank[0].key : "education");
  const secondaryKey =
    sectorRank.find((item) => item.key !== primaryKey && item.score > 0)?.key ||
    (primaryKey === "education" ? "small_industries" : "education");

  const supportRank = sortByScore(scores, Object.keys(SUPPORT_LABELS)).filter(
    (item) => item.score > 0
  );
  const supportSectorKey =
    supportRank.map((item) => SUPPORT_TO_SECTOR[item.key]).find(Boolean) ||
    (scores.finance_support > 0 ? "finance" : "education");

  const primary = SECTORS[primaryKey];
  const secondary = SECTORS[secondaryKey];
  const supportSector = SECTORS[supportSectorKey];
  const snapshot = buildSnapshot(primaryKey, supportRank, answers, scores);
  const intentCopy = getIntentResultCopy(intentId);
  const topSectorScore = Math.max(1, sectorRank[0]?.score || 1);
  const sectorFit = (isGuidanceOnly || mentorPathwayReport) ? [] : sectorRank
    .filter((item) => item.score > 0)
    .slice(0, 3)
    .map((item) => ({
      ...SECTORS[item.key],
      percent: Math.max(38, Math.min(92, Math.round((item.score / topSectorScore) * 88))),
    }));

  if (!sectorFit.length && !isGuidanceOnly && !mentorPathwayReport) {
    sectorFit.push({ ...SECTORS.education, percent: 70 });
  }

  const opportunityAssessment = mentorPathwayReport
    ? { recommendations: [], evidenceConfidence: 0 }
    : buildOpportunityAssessment({ guidanceOnly: isGuidanceOnly, answers });

  const reportTemplate = mentorPathwayReport
    ? mentorPathwayReport.pathwayType === "paid_coaching"
      ? "paid_coaching"
      : ["internship_provider", "talent_pipeline"].includes(mentorPathwayReport.pathwayType)
        ? "internship_provider"
        : "mentor_volunteer"
    : userIntent === "business_growth"
      ? "existing_business"
      : "supplemental_income";

  return {
    userIntent,
    supportNeeds: supportRank.slice(0, 5).map((item) => SUPPORT_LABELS[item.key]).filter(Boolean),
    currentCareerProfile: {
      situation: answers.career_situation || "",
      broadField: answers.career_field || "",
      householdDependence: answers.household_dependence || "",
    },
    capabilityProfile: {
      demonstratedCapabilities: Array.isArray(answers.practical_exposure) ? answers.practical_exposure : [],
      proficiency: answers.transferable_depth || "Not assessed",
      evidence: Array.isArray(answers.responsibilities) ? answers.responsibilities : [],
      recency: "Initial assessment; evidence can be provided later",
      credentials: [],
      transferableCapabilities: Array.isArray(answers.responsibilities) ? answers.responsibilities : [],
    },
    nonITOpportunityProfile: {
      preferredPath: answers.v2_non_it_preference || "",
      exploredAreas: Array.isArray(answers.v2_sector_exploration) ? answers.v2_sector_exploration : [],
      classificationPriority: "NON_IT → TECHNOLOGY_ENABLED_NON_IT → IT_ADJACENT",
    },
    coreBusinessVision: answers.vision || "",
    lifeJourneyProfile: { consent: answers.life_support_consent || "Not requested in this assessment" },
    ecosystemContributionProfile: Array.isArray(answers.ecosystem_contribution) ? answers.ecosystem_contribution : [],
    opportunityReadiness: isGuidanceOnly ? "guidance_only" : (answers.v2_12m_goal?.includes("Test a business") || scores.action_ready > 2 ? "ready_to_test" : answers.v2_12m_goal?.includes("license") ? "learning_first" : "mentored_exploration"),
    evidenceConfidence: "Low — demonstrated examples or credentials were not collected in this assessment.",
    opportunityAssessment,
    pathwayType: mentorPathwayReport?.pathwayType ||
      (userIntent === "business_growth" ? "business_opportunity" : userIntent === "second_income" || userIntent === "mixed" ? "supplemental_income" : "undecided"),
    reportTemplate,
    mentorPathwayReport,
    primary,
    secondary,
    supportSector,
    intent: intent || getIntentById("unsure"),
    intentCopy,
    snapshot,
    sectorFit,
    reasons: [...new Set(reasons)].slice(0, 5),
    // A visitor-selected V2 sector is an explicit focus, not a hint to mix
    // unrelated secondary-sector ideas into the report.
    paths: mentorPathwayReport ? [] : explicitPrimaryKey ? primary.paths : [...primary.paths, ...secondary.paths].slice(0, 5),
    supportTags: supportRank.slice(0, 4).map((item) => SUPPORT_LABELS[item.key]),
  };
};

const OpportunityCompanion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const suppressPromotions = location.pathname === "/2.0/claude-practice-test";
  const { isAuthenticated, token } = useContext(AuthContext);
  const returningToProfile = Boolean(location.state?.openOpportunityProfile);
  const [isOpen, setIsOpen] = useState(returningToProfile);
  const [showCompanionInvite, setShowCompanionInvite] = useState(false);
  const [mode, setMode] = useState(returningToProfile ? "profileGate" : "intro");
  const [visitorIntent, setVisitorIntent] = useState(null);
  const [showIntentPrompt, setShowIntentPrompt] = useState(
    () => !returningToProfile && !hasSeenIntentPrompt()
  );
  const [allowRegistrationCompletionPrompt, setAllowRegistrationCompletionPrompt] = useState(false);
  const [experiencePrompt, setExperiencePrompt] = useState("hidden");
  const [experienceSurveyAnswers, setExperienceSurveyAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [startedAt, setStartedAt] = useState(null);
  const [result, setResult] = useState(null);
  const [isProfileJourney, setIsProfileJourney] = useState(returningToProfile);
  const [savedProfile, setSavedProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");
  const [contactNotes, setContactNotes] = useState("");
  const [contactRequest, setContactRequest] = useState(null);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [reportName, setReportName] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [expertDiscussion, setExpertDiscussion] = useState(true);
  const [reportSending, setReportSending] = useState(false);
  const [reportMessage, setReportMessage] = useState("");
  const [reportClaimToken, setReportClaimToken] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("claimReport") || safeStorageGet(window.localStorage, REPORT_CLAIM_STORAGE_KEY) || "";
  });
  const [needsHumanCheck, setNeedsHumanCheck] = useState(false);
  const [humanCheckError, setHumanCheckError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const intentContentRef = useRef(null);
  const reportNameRef = useRef(null);
  const reportEmailRef = useRef(null);
  const exitIntentReadyAtRef = useRef(
    Date.now() + EXPERIENCE_EXIT_INTENT_MIN_DWELL_MS
  );
  const returnFromBackgroundRef = useRef(false);

  const activeQuestions = useMemo(
    () => getQuestionFlow(visitorIntent, answers),
    [answers, visitorIntent]
  );
  const activeQuestion = activeQuestions[step] || activeQuestions[0];
  const progress = Math.round(((step + 1) / activeQuestions.length) * 100);
  const isFinished = Boolean(result);
  const selectedCount = Object.keys(answers).filter((key) => key !== "intentId").length;
  const currentIntent = getIntentById(visitorIntent || answers.intentId);
  const shouldShowIntentPrompt =
    showIntentPrompt &&
    !isOpen &&
    location.pathname.startsWith("/2.0") &&
    !suppressPromotions &&
    (!isRegistrationRoute(location.pathname) || allowRegistrationCompletionPrompt);

  const currentResult = useMemo(
    () => result || (selectedCount ? buildRecommendations(answers) : null),
    [answers, result, selectedCount]
  );
  const mentorPathway = currentResult?.mentorPathwayReport || null;
  const hasIncomeOpportunityReport = Boolean(
    currentResult && ["supplemental_income", "existing_business"].includes(currentResult.reportTemplate)
  );

  useEffect(() => {
    const queryToken = new URLSearchParams(location.search).get("claimReport");
    if (queryToken) {
      setReportClaimToken(queryToken);
      window.localStorage.setItem(REPORT_CLAIM_STORAGE_KEY, queryToken);
    }
  }, [location.search]);

  useEffect(() => {
    const openIntentPromptFromTour = () => {
      if (suppressPromotions || returningToProfile || isRegistrationRoute(location.pathname)) return;
      setExperiencePrompt("hidden");
      setIsOpen(false);
      setShowIntentPrompt(true);
    };

    const openIntentPromptAfterRegistration = () => {
      if (suppressPromotions || returningToProfile) return;
      setExperiencePrompt("hidden");
      setIsOpen(false);
      setAllowRegistrationCompletionPrompt(true);
      setShowIntentPrompt(true);
    };

    window.addEventListener("udyog2-open-intent-prompt", openIntentPromptFromTour);
    window.addEventListener("udyog2-open-intent-prompt-after-registration", openIntentPromptAfterRegistration);
    return () => {
      window.removeEventListener("udyog2-open-intent-prompt", openIntentPromptFromTour);
      window.removeEventListener("udyog2-open-intent-prompt-after-registration", openIntentPromptAfterRegistration);
    };
  }, [location.pathname, returningToProfile, suppressPromotions]);

  useEffect(() => {
    const ownsPromotionLock =
      isOpen || shouldShowIntentPrompt || experiencePrompt !== "hidden";

    if (ownsPromotionLock) {
      window.sessionStorage.setItem(PROMOTION_LOCK_STORAGE_KEY, "uc-companion");
    } else if (
      window.sessionStorage.getItem(PROMOTION_LOCK_STORAGE_KEY) === "uc-companion"
    ) {
      window.sessionStorage.removeItem(PROMOTION_LOCK_STORAGE_KEY);
    }

    return () => {
      if (
        window.sessionStorage.getItem(PROMOTION_LOCK_STORAGE_KEY) === "uc-companion"
      ) {
        window.sessionStorage.removeItem(PROMOTION_LOCK_STORAGE_KEY);
      }
    };
  }, [experiencePrompt, isOpen, shouldShowIntentPrompt]);

  useEffect(() => {
    if (isRegistrationRoute(location.pathname)) {
      setShowCompanionInvite(false);
      return undefined;
    }

    let hideTimer;
    let retryTimer;

    if (
      suppressPromotions ||
      isOpen ||
      shouldShowIntentPrompt ||
      experiencePrompt !== "hidden" ||
      window.sessionStorage.getItem(COMPANION_INVITE_SESSION_KEY) === "yes"
    ) {
      setShowCompanionInvite(false);
      return undefined;
    }

    const releasePromotionLock = () => {
      if (
        window.sessionStorage.getItem(PROMOTION_LOCK_STORAGE_KEY) ===
        "uc-companion-reminder"
      ) {
        window.sessionStorage.removeItem(PROMOTION_LOCK_STORAGE_KEY);
      }
    };

    const showOnceThisSession = () => {
      if (window.sessionStorage.getItem(PROMOTION_LOCK_STORAGE_KEY)) {
        retryTimer = window.setTimeout(showOnceThisSession, PROMOTION_RETRY_DELAY_MS);
        return;
      }

      window.sessionStorage.setItem(COMPANION_INVITE_SESSION_KEY, "yes");
      window.sessionStorage.setItem(
        PROMOTION_LOCK_STORAGE_KEY,
        "uc-companion-reminder"
      );
      setShowCompanionInvite(true);
      hideTimer = window.setTimeout(() => {
        setShowCompanionInvite(false);
        releasePromotionLock();
      }, COMPANION_INVITE_DURATION_MS);
    };

    const firstShowTimer = window.setTimeout(
      showOnceThisSession,
      COMPANION_INVITE_DELAY_MS
    );

    return () => {
      window.clearTimeout(firstShowTimer);
      window.clearTimeout(retryTimer);
      window.clearTimeout(hideTimer);
      releasePromotionLock();
    };
  }, [
    experiencePrompt,
    isOpen,
    location.pathname,
    shouldShowIntentPrompt,
    suppressPromotions,
  ]);

  useEffect(() => {
    if (returningToProfile) return undefined;
    if (!location.pathname.startsWith("/2.0")) return undefined;
    if (isRegistrationRoute(location.pathname)) return undefined;
    if (suppressPromotions) return undefined;
    if (isOpen || showIntentPrompt || experiencePrompt !== "hidden") return undefined;
    if (!hasSeenIntentPrompt() || hasCompletedExperienceSurvey()) return undefined;
    if (
      window.sessionStorage.getItem(EXPERIENCE_EXIT_PROMPT_SESSION_KEY) === "yes"
    ) {
      return undefined;
    }

    const showExitPrompt = () => {
      if (Date.now() < exitIntentReadyAtRef.current) return;
      if (window.sessionStorage.getItem(PROMOTION_LOCK_STORAGE_KEY)) return;
      if (
        window.sessionStorage.getItem(EXPERIENCE_EXIT_PROMPT_SESSION_KEY) ===
        "yes"
      ) {
        return;
      }

      window.sessionStorage.setItem(
        EXPERIENCE_EXIT_PROMPT_SESSION_KEY,
        "yes"
      );
      setExperiencePrompt("nudge");
    };

    const handlePointerExit = (event) => {
      if (event.relatedTarget || event.clientY > 0) return;
      showExitPrompt();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        returnFromBackgroundRef.current = true;
        return;
      }
      if (returnFromBackgroundRef.current) {
        returnFromBackgroundRef.current = false;
        showExitPrompt();
      }
    };

    document.addEventListener("mouseout", handlePointerExit);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("mouseout", handlePointerExit);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    experiencePrompt,
    isOpen,
    location.pathname,
    returningToProfile,
    showIntentPrompt,
    suppressPromotions,
  ]);

  const buildIntentAnswerSeed = (intentId) => {
    const intent = getIntentById(intentId);
    return intent ? { intent: intent.label, intentId: intent.id } : {};
  };

  const dismissIntentPrompt = () => {
    rememberIntentPrompt();
    setShowIntentPrompt(false);
    setAllowRegistrationCompletionPrompt(false);
  };

  const dismissCompanionInvite = () => {
    setShowCompanionInvite(false);
    if (
      window.sessionStorage.getItem(PROMOTION_LOCK_STORAGE_KEY) ===
      "uc-companion-reminder"
    ) {
      window.sessionStorage.removeItem(PROMOTION_LOCK_STORAGE_KEY);
    }
  };

  const openClaudePracticeTest = () => {
    dismissIntentPrompt();
    navigate("/2.0/claude-practice-test");
  };

  const reopenIntentPrompt = () => {
    setExperiencePrompt("hidden");
    setIsOpen(false);
    setShowIntentPrompt(true);
  };

  const closeExperiencePrompt = () => {
    setExperiencePrompt("hidden");
  };

  const selectExperienceAnswer = (key, value) => {
    setExperienceSurveyAnswers((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const startExperienceSurvey = (foundValue) => {
    setExperienceSurveyAnswers((current) => ({
      ...current,
      found: foundValue,
      path: location.pathname,
    }));
    setExperiencePrompt("survey");
  };

  const submitExperienceSurvey = () => {
    rememberExperienceSurvey({
      ...experienceSurveyAnswers,
      path: location.pathname,
    });
    setExperiencePrompt("thanks");
  };

  const startIntentJourney = (intentId) => {
    dismissIntentPrompt();
    setIsOpen(true);
    setIsProfileJourney(false);
    setVisitorIntent(intentId);
    setMode("explore");
    setStep(0);
    setAnswers(buildIntentAnswerSeed(intentId));
    setResult(null);
    setSavedProfile(null);
    setProfileMessage("");
    setContactRequest(null);
    setContactNotes("");
    setNeedsHumanCheck(false);
    setHumanCheckError("");
    setStartedAt(Date.now());
  };

  const startExplore = () => {
    setIsProfileJourney(false);
    setVisitorIntent(null);
    setMode("explore");
    setStep(0);
    setAnswers({});
    setResult(null);
    setNeedsHumanCheck(false);
    setHumanCheckError("");
    setStartedAt(Date.now());
  };

  const startProfileAssessment = (intentId = visitorIntent) => {
    const seed = buildIntentAnswerSeed(intentId);
    setIsProfileJourney(true);
    setVisitorIntent(intentId || null);
    setMode("explore");
    setStep(0);
    setAnswers(seed);
    setResult(null);
    setSavedProfile(null);
    setProfileMessage("");
    setContactRequest(null);
    setContactNotes("");
    setNeedsHumanCheck(false);
    setHumanCheckError("");
    setStartedAt(Date.now());
  };

  const startProfile = async () => {
    if (!isAuthenticated) {
      setMode("profileGate");
      return;
    }

    setIsProfileJourney(true);
    setProfileLoading(true);
    setProfileMessage("");
    try {
      const authToken = token || localStorage.getItem("token");
      const claimToken = reportClaimToken || new URLSearchParams(location.search).get("claimReport");
      if (claimToken) {
        try {
          const claimResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/opportunity-companion/claim-report`,
            { claimToken },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
          setSavedProfile(claimResponse.data?.profile || null);
          setProfileMessage(claimResponse.data?.message || "Your emailed report was attached to your UC profile.");
          setReportClaimToken("");
          window.localStorage.removeItem(REPORT_CLAIM_STORAGE_KEY);
        } catch (claimError) {
          setProfileMessage(claimError?.response?.data?.message || "Your report could not be attached yet.");
        }
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/opportunity-companion/profile`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const profile = response.data?.profile;
      if (profile?.answers && Object.keys(profile.answers).length) {
        const savedIntentId =
          profile.answers.intentId || findIntentIdFromLabel(profile.answers.intent);
        setVisitorIntent(savedIntentId);
        setAnswers(profile.answers);
        setResult(buildRecommendations(profile.answers));
        setSavedProfile(profile);
        setContactRequest(profile.contactRequest || null);
        setContactNotes(profile.contactRequest?.notes || "");
        setMode("explore");
        setProfileMessage("Your saved opportunity profile is ready.");
      } else {
        startProfileAssessment();
      }
    } catch (error) {
      setMode("profileGate");
      setProfileMessage(
        error?.response?.data?.message || "Unable to load your saved profile."
      );
    } finally {
      setProfileLoading(false);
    }
  };

  const saveOpportunityProfile = async (nextAnswers, nextResult) => {
    setProfileSaving(true);
    setProfileMessage("Saving your opportunity profile…");
    try {
      const authToken = token || localStorage.getItem("token");
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/opportunity-companion/profile`,
        { answers: nextAnswers, result: nextResult },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setSavedProfile(response.data?.profile || null);
      setContactRequest(response.data?.profile?.contactRequest || null);
      setProfileMessage("Profile saved. You can return and update it anytime.");
    } catch (error) {
      setProfileMessage(
        error?.response?.data?.message ||
          "Your result is ready, but the profile could not be saved."
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const finishWithAnswers = (nextAnswers) => {
    const tooFast = startedAt && Date.now() - startedAt < 3000;
    if (tooFast || honeypot.trim()) {
      setNeedsHumanCheck(true);
      return;
    }

    const nextResult = buildRecommendations(nextAnswers);
    setResult(nextResult);
    if (isProfileJourney && isAuthenticated) {
      saveOpportunityProfile(nextAnswers, nextResult);
    }
  };

  const selectOption = (label) => {
    const selectedOption = activeQuestion.options.find((option) => option.label === label);

    if (activeQuestion.answerType?.startsWith("multi_select")) {
      const current = Array.isArray(answers[activeQuestion.id]) ? answers[activeQuestion.id] : [];
      const nextValues = updateMultiSelectAnswer(
        current,
        label,
        activeQuestion.selectionLimit || 99
      );
      setAnswers((currentAnswers) => ({ ...currentAnswers, [activeQuestion.id]: nextValues }));
      return;
    }

    const nextAnswers = {
      ...answers,
      [activeQuestion.id]: label,
    };

    if (activeQuestion.id === "intent") {
      const intentId = selectedOption?.intentId || findIntentIdFromLabel(label);
      setVisitorIntent(intentId);
      setAnswers({
        ...nextAnswers,
        ...buildIntentAnswerSeed(intentId),
      });
      setStep(0);
      return;
    }

    setAnswers(nextAnswers);

    if (step >= activeQuestions.length - 1) {
      finishWithAnswers(nextAnswers);
      return;
    }

    setStep((current) => current + 1);
  };

  const continueMultiSelect = () => {
    const selected = answers[activeQuestion.id];
    if (!Array.isArray(selected) || !selected.length) return;
    if (step >= activeQuestions.length - 1) {
      finishWithAnswers(answers);
      return;
    }
    setStep((current) => current + 1);
  };

  const goBack = () => {
    if (step === 0) {
      setMode("intro");
      return;
    }
    const previousQuestion = activeQuestions[step - 1];
    setAnswers((current) => {
      const copy = { ...current };
      delete copy[previousQuestion.id];
      return copy;
    });
    setStep((current) => Math.max(0, current - 1));
  };

  const answerHumanCheck = (answer) => {
    if (answer !== HUMAN_CHECK.answer) {
      setHumanCheckError("Please choose the real-life goal to continue.");
      return;
    }
    setHumanCheckError("");
    setNeedsHumanCheck(false);
    const nextResult = buildRecommendations(answers);
    setResult(nextResult);
    if (isProfileJourney && isAuthenticated) {
      saveOpportunityProfile(answers, nextResult);
    }
  };

  const goToLogin = () => {
    const returnPath =
      location.pathname === "/2.0/login"
        ? "/2.0/udyogconnect-home"
        : location.pathname;
    navigate("/2.0/login", {
      state: {
        from: {
          pathname: returnPath,
          state: { openOpportunityProfile: true },
        },
      },
    });
  };

  const submitContactRequest = async () => {
    if (!savedProfile || contactSubmitting) return;
    setContactSubmitting(true);
    setProfileMessage("");
    try {
      const authToken = token || localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/opportunity-companion/contact-request`,
        { notes: contactNotes },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setContactRequest(response.data?.contactRequest || null);
      setProfileMessage(response.data?.message || "Your request was sent to UC.");
    } catch (error) {
      setProfileMessage(
        error?.response?.data?.message || "Unable to send your UC contact request."
      );
    } finally {
      setContactSubmitting(false);
    }
  };

  const sendFreeReport = async (event) => {
    event?.preventDefault();
    if (reportSending || !currentResult) return;
    const recipient = validateReportRecipient({
      name: getEffectiveInputValue(reportName, reportNameRef.current),
      email: getEffectiveInputValue(reportEmail, reportEmailRef.current),
    });
    if (!recipient.valid) {
      setReportMessage(recipient.message);
      return;
    }
    setReportSending(true);
    setReportMessage("");
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/opportunity-companion/report-email`, {
        name: recipient.name,
        email: recipient.email,
        answers,
        expertDiscussion,
        result: currentResult,
      }, { timeout: 20000 });
      const claimToken = response.data?.claimToken || "";
      if (claimToken) {
        setReportClaimToken(claimToken);
        safeStorageSet(window.localStorage, REPORT_CLAIM_STORAGE_KEY, claimToken);
      }
      setReportMessage(response.data?.message || "Your free report was sent.");
    } catch (error) {
      setReportMessage(
        error?.code === "ECONNABORTED"
          ? "The email request timed out. Please try again."
          : error?.response?.data?.message || "Unable to send your report right now."
      );
    } finally {
      setReportSending(false);
    }
  };

  const goToPrimarySector = () => {
    if (!currentResult?.primary) return;
    navigate(`/2.0/industry/${currentResult.primary.route}`);
  };

  const goToCommunityMeetups = () => {
    setIsOpen(false);
    navigate({
      pathname: "/2.0/calendar",
      hash: "#uc-community-meetups",
    });
  };

  return suppressPromotions ? null : (
    <div className={`uc-companion ${isOpen ? "open" : ""}`}>
      {shouldShowIntentPrompt && (
        <section className="uc-intent-backdrop" aria-label="Udyog Connect visit helper">
          <div className="uc-intent-modal" data-tour="uc-intent-entry" role="dialog" aria-modal="true">
            <button
              type="button"
              className="uc-intent-close"
              onClick={dismissIntentPrompt}
              aria-label="Close Udyog Connect helper"
            >
              <Close fontSize="small" />
            </button>

            <div className="uc-intent-content" ref={intentContentRef}>
            <div className="uc-intent-hero">
              <span className="uc-intent-kicker">
                <VolunteerActivism fontSize="inherit" />
                UC Opportunity Companion
              </span>
              <h2>What brought you to Udyog Connect today?</h2>
              <p>
                Before you leave, let UC give you one practical direction — a
                second-income idea, student/internship path, family support
                route, or the right professional connection.
              </p>
              <div className="uc-intent-hero-actions">
                <div className="uc-intent-promise">
                  <span>60-second assessment</span>
                  <span>No login needed to explore</span>
                  <span>Sign in only when you want UC to connect you</span>
                </div>
                <button
                  type="button"
                  className="uc-claude-practice-cta"
                  onClick={openClaudePracticeTest}
                >
                  <span className="uc-claude-practice-icon" aria-hidden="true">
                    <Psychology fontSize="small" />
                  </span>
                  <span>
                    <strong>UC Claude Practice Test</strong>
                    <small>Prepare for Architect – Foundations</small>
                  </span>
                  <b aria-hidden="true">→</b>
                </button>
              </div>
            </div>

            <div className="uc-intent-grid">
              {INTENT_OPTIONS.map(({ id, label, description, Icon }) => (
                <button
                  type="button"
                  key={id}
                  className="uc-intent-option"
                  onClick={() => startIntentJourney(id)}
                >
                  <span className="uc-intent-option-icon">
                    <Icon fontSize="small" />
                  </span>
                  <span>
                    <strong>{label}</strong>
                    <small>{description}</small>
                  </span>
                </button>
              ))}
            </div>

            <div className="uc-intent-actions">
              <button type="button" className="uc-companion-secondary" onClick={dismissIntentPrompt}>
                Explore UC first
              </button>
              <button
                type="button"
                className="uc-companion-primary"
                onClick={() => startIntentJourney("unsure")}
              >
                Show me my path
              </button>
            </div>
            </div>
            <PopupScrollRail scrollRef={intentContentRef} />
          </div>
        </section>
      )}

      {experiencePrompt !== "hidden" && !shouldShowIntentPrompt && !isOpen && (
        <aside className="uc-experience-nudge" aria-label="Udyog Connect experience check">
          <button
            type="button"
            className="uc-experience-close"
            onClick={closeExperiencePrompt}
            aria-label="Close visitor feedback prompt"
          >
            <Close fontSize="small" />
          </button>

          {experiencePrompt === "nudge" && (
            <>
              <span className="uc-experience-kicker">Quick check</span>
              <h3>Did you get what you were looking for?</h3>
              <p>
                If not, UC Companion can guide you back to the right path in a
                few clicks.
              </p>
              <div className="uc-experience-actions">
                <button type="button" onClick={() => startExperienceSurvey("yes")}>
                  Yes
                </button>
                <button type="button" onClick={() => startExperienceSurvey("not_yet")}>
                  Not yet
                </button>
                <button type="button" className="primary" onClick={reopenIntentPrompt}>
                  Guide me
                </button>
              </div>
            </>
          )}

          {experiencePrompt === "survey" && (
            <>
              <span className="uc-experience-kicker">10-second survey</span>
              <h3>How was your UC visit so far?</h3>

              <div className="uc-experience-question">
                <span>Experience</span>
                <div>
                  {["Clear and helpful", "Still exploring", "Could not find it"].map((label) => (
                    <button
                      type="button"
                      key={label}
                      className={
                        experienceSurveyAnswers.experience === label ? "selected" : ""
                      }
                      onClick={() => selectExperienceAnswer("experience", label)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="uc-experience-question">
                <span>What were you looking for?</span>
                <div>
                  {[
                    "Income idea",
                    "Student/internship",
                    "Immigration/legal",
                    "Family guidance",
                    "Partner/provider info",
                  ].map((label) => (
                    <button
                      type="button"
                      key={label}
                      className={experienceSurveyAnswers.lookingFor === label ? "selected" : ""}
                      onClick={() => selectExperienceAnswer("lookingFor", label)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="uc-experience-actions">
                <button type="button" onClick={reopenIntentPrompt}>
                  Open guide
                </button>
                <button type="button" className="primary" onClick={submitExperienceSurvey}>
                  Submit
                </button>
              </div>
            </>
          )}

          {experiencePrompt === "thanks" && (
            <>
              <span className="uc-experience-kicker">Thank you</span>
              <h3>This helps UC improve.</h3>
              <p>
                Your feedback tells us what visitors need before they register
                or ask for a connection.
              </p>
              <div className="uc-experience-actions">
                <button type="button" onClick={reopenIntentPrompt}>
                  Open guide
                </button>
                <button type="button" className="primary" onClick={closeExperiencePrompt}>
                  Close
                </button>
              </div>
            </>
          )}
        </aside>
      )}

      {!isOpen && (
        <>
          {showCompanionInvite && (
            <div className="uc-companion-invite" role="note">
              <button
                type="button"
                className="uc-companion-invite-close"
                onClick={dismissCompanionInvite}
                aria-label="Dismiss UC Companion invitation"
                title="Dismiss invitation"
              >
                <Close fontSize="inherit" />
              </button>
              <strong>Your UC Opportunity Companion</strong>
              <span>A friend in need is a friend indeed — see what’s possible for you.</span>
            </div>
          )}
          <button
            type="button"
            className="uc-companion-fab"
            data-tour="uc-companion-fab"
            onClick={() => setIsOpen(true)}
            aria-label="Open UC Opportunity Companion"
            aria-expanded={isOpen}
            title="Find Your Opportunity & Impact"
          >
            <span className="uc-companion-fab-icon" aria-hidden="true">
              <span className="uc-money-icon">
                <span className="uc-money-bill uc-money-bill-one">$</span>
                <span className="uc-money-bill uc-money-bill-two">$</span>
                <span className="uc-money-bill uc-money-bill-three">$</span>
                <span className="uc-money-stack">
                  <span className="uc-money-stack-band">$</span>
                </span>
              </span>
            </span>
            <span className="uc-companion-fab-copy">
              <strong>Find Your Opportunity &amp; Impact</strong>
              <small>60-second opportunity match</small>
            </span>
            <span className="uc-companion-fab-badge">Start</span>
          </button>
        </>
      )}

      {isOpen && (
        <section className="uc-companion-panel" aria-label="UC Opportunity Companion">
          <header className="uc-companion-header">
            <div>
              <span className="uc-companion-kicker">
                <VolunteerActivism fontSize="inherit" />
                Nonprofit opportunity guide
              </span>
              <h2>UC Opportunity Companion</h2>
            </div>
            <button
              type="button"
              className="uc-companion-icon-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close UC Opportunity Companion"
            >
              <Close fontSize="small" />
            </button>
          </header>

          <input
            className="uc-companion-honeypot"
            tabIndex="-1"
            autoComplete="off"
            value={honeypot}
            onChange={(event) => setHoneypot(event.target.value)}
            aria-hidden="true"
          />

          {mode === "intro" && (
            <div className="uc-companion-body">
              <div className="uc-companion-welcome">
                <div className="uc-companion-orb">
                  <Lightbulb />
                </div>
                <p>
                  I can help you explore practical second-income ideas,
                  entrepreneurship paths, and UC connections based on your
                  interests.
                </p>
              </div>

              {currentIntent && (
                <div className="uc-companion-intent-chip">
                  <span>Current path</span>
                  <strong>{currentIntent.shortLabel}</strong>
                  <small>{currentIntent.description}</small>
                </div>
              )}

              <div className="uc-companion-mode-grid">
                <button type="button" onClick={startExplore}>
                  <Lightbulb fontSize="small" />
                  <span>
                    <strong>Explore Ideas</strong>
                    <small>Anonymous, quick, no login needed</small>
                  </span>
                </button>
                <button type="button" onClick={() => setMode("profileGate")}>
                  <Psychology fontSize="small" />
                  <span>
                    <strong>Build My Opportunity Profile</strong>
                    <small>Requires login/register</small>
                  </span>
                </button>
                <button type="button" onClick={goToCommunityMeetups}>
                  <EventAvailable fontSize="small" />
                  <span>
                    <strong>Attend UC Community Meetup</strong>
                    <small>Free city-center, library, and online sessions</small>
                  </span>
                </button>
              </div>
            </div>
          )}

          {mode === "profileGate" && (
            <div className="uc-companion-body">
              <div className="uc-companion-message-card">
                <Psychology />
                <h3>Build your Opportunity Profile</h3>
                <p>
                  Save your answers, return to update them, and request an
                  introduction to the right UC community or sector contact.
                </p>
                {isAuthenticated ? (
                  <button
                    type="button"
                    className="uc-companion-primary"
                    onClick={startProfile}
                    disabled={profileLoading}
                  >
                    <Psychology fontSize="small" />
                    {profileLoading ? "Loading profile…" : "Start / Resume My Profile"}
                  </button>
                ) : (
                  <button type="button" className="uc-companion-primary" onClick={goToLogin}>
                    <Login fontSize="small" />
                    Sign in / Register
                  </button>
                )}
                {profileMessage && (
                  <p className="uc-companion-error">{profileMessage}</p>
                )}
                <button type="button" className="uc-companion-secondary" onClick={startExplore}>
                  Continue exploring anonymously
                </button>
              </div>
            </div>
          )}

          {mode === "explore" && !isFinished && !needsHumanCheck && (
            <div className="uc-companion-body">
              <div className="uc-companion-progress" aria-label={`Progress ${progress}%`}>
                <span style={{ width: `${progress}%` }} />
              </div>

              <div className="uc-companion-question-meta">
                <button type="button" onClick={goBack}>
                  <KeyboardArrowLeft fontSize="small" />
                  Back
                </button>
                <span>
                  Question {step + 1} of {activeQuestions.length}
                </span>
              </div>

              <div className="uc-companion-question">
                <span>{activeQuestion.eyebrow}</span>
                <h3>{activeQuestion.text}</h3>
                {activeQuestion.answerType?.startsWith("multi_select") && (
                  <p className="uc-companion-selection-hint">
                    Select up to {activeQuestion.selectionLimit || 3} options, then choose Continue.
                  </p>
                )}
              </div>

              <div className="uc-companion-options">
                {activeQuestion.options.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    className={
                      Array.isArray(answers[activeQuestion.id]) && answers[activeQuestion.id].includes(option.label)
                        ? "selected"
                        : ""
                    }
                    aria-pressed={
                      Array.isArray(answers[activeQuestion.id]) && answers[activeQuestion.id].includes(option.label)
                    }
                    onClick={() => selectOption(option.label)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {activeQuestion.answerType?.startsWith("multi_select") && (
                <button
                  type="button"
                  className="uc-companion-primary"
                  onClick={continueMultiSelect}
                  disabled={!Array.isArray(answers[activeQuestion.id]) || !answers[activeQuestion.id].length}
                >
                  {Array.isArray(answers[activeQuestion.id]) && answers[activeQuestion.id].length
                    ? "Continue"
                    : "Select at least one option"}
                </button>
              )}
            </div>
          )}

          {needsHumanCheck && (
            <div className="uc-companion-body">
              <div className="uc-companion-question">
                <span>Quick human check</span>
                <h3>{HUMAN_CHECK.text}</h3>
                <p>
                  This helps us keep the Companion useful for real community
                  visitors.
                </p>
              </div>
              <div className="uc-companion-options">
                {HUMAN_CHECK.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => answerHumanCheck(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {humanCheckError && (
                <p className="uc-companion-error">{humanCheckError}</p>
              )}
            </div>
          )}

          {isFinished && currentResult && (
            <div className="uc-companion-body result">
              {isProfileJourney && (
                <div className="uc-companion-profile-status" aria-live="polite">
                  <CheckCircle fontSize="small" />
                  <div>
                    <strong>
                      {profileSaving
                        ? "Saving your profile"
                        : savedProfile
                          ? "Opportunity profile saved"
                          : "Opportunity profile"}
                    </strong>
                    <small>
                      {profileMessage ||
                        "Your answers and opportunity map stay connected to your account."}
                    </small>
                  </div>
                </div>
              )}
              <div className="uc-companion-result-hero">
                <span>
                  <TrendingUp fontSize="small" />
                  {mentorPathway ? "UC contribution pathway" : currentResult.intentCopy.title}
                </span>
                <h3>{mentorPathway
                  ? mentorPathway.title
                  : currentResult.userIntent === "family_guidance"
                    ? "Your UC support-navigation report"
                    : `${currentResult.opportunityAssessment.recommendations[0]?.label || "A specific UC opportunity"} is a preliminary pathway.`}</h3>
                <p>
                  {mentorPathway
                    ? mentorPathway.summary
                    : <>{currentResult.intentCopy.nextAction} These are planning estimates, not guaranteed income.</>}
                </p>
              </div>

              {!mentorPathway && hasIncomeOpportunityReport && currentResult.sectorFit?.length > 0 && (
                <section className="uc-companion-sector-match" aria-label="UC sector match summary">
                  <div className="uc-companion-section-eyebrow">Sector match</div>
                  <h4>Your strongest UC sector matches</h4>
                  <p className="uc-companion-sector-match-intro">
                    A quick signal from your answers. These percentages are preliminary and should be validated with a UC mentor.
                  </p>
                  <div className="uc-companion-sector-match-grid">
                    {currentResult.sectorFit.map((sector, index) => (
                      <div className="uc-companion-sector-match-card" key={`${sector.route}-${index}`}>
                        <div className="uc-companion-sector-match-card-top">
                          <span className="uc-companion-sector-dot" style={{ background: sector.color || "#1746d1" }} />
                          <strong>{sector.label}</strong>
                          <b>{sector.percent}%</b>
                        </div>
                        <div className="uc-companion-match-meter" aria-hidden="true">
                          <span style={{ width: `${sector.percent}%`, background: sector.color || "#1746d1" }} />
                        </div>
                        <small>{index === 0 ? "Best immediate pathway" : index === 1 ? "Strong alternative" : "Explore after validation"}</small>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {currentResult.intent && (
                <div className="uc-companion-pathway-card">
                  <span>Visitor pathway</span>
                  <strong>{currentResult.intent.shortLabel}</strong>
                  <small>{currentResult.intent.description}</small>
                </div>
              )}

              {currentResult.userIntent === "family_guidance" && (
                <section className="uc-companion-support-report">
                  <h4>Primary support needs</h4>
                  <p>{currentResult.supportNeeds.join(", ") || "A UC support conversation"}</p>
                  <small>UC can help prepare questions and route healthcare, education, finance, insurance, or legal topics to qualified professionals. These needs are not business-capability evidence.</small>
                </section>
              )}

              {mentorPathway ? (
                <section className="uc-companion-support-report">
                  <h4>Assessment status: {mentorPathway.assessmentStatus}</h4>
                  <p><strong>What you expressed interest in:</strong> {mentorPathway.expressedInterest}</p>
                  {!mentorPathway.assessmentComplete && (
                    <>
                      <h4>Information still needed</h4>
                      <ul>{mentorPathway.informationNeeded.map((item) => <li key={item}>{item}</li>)}</ul>
                    </>
                  )}
                  <h4>Possible UC roles</h4>
                  <ul>{mentorPathway.possibleRoles.map((role) => <li key={role}>{role}</li>)}</ul>
                  <h4>Next questions</h4>
                  <ul>{mentorPathway.nextQuestions.map((question) => <li key={question}>{question}</li>)}</ul>
                  <p><strong>UC connection option:</strong> {mentorPathway.connectionOption}</p>
                </section>
              ) : null}

              {hasIncomeOpportunityReport && currentResult.opportunityAssessment.recommendations.length > 0 && <div className="uc-companion-sector-list">
                <div className="uc-companion-specific-opportunities-heading">
                  <h4>Specific opportunity ideas</h4>
                  <p>These ideas are based on the practical preferences and experience you shared.</p>
                </div>
                {currentResult.opportunityAssessment.recommendations.slice(0, 3).map((opportunity) => (
                  <div className="uc-companion-sector-fit" key={opportunity.id}>
                    <div>
                      <span style={{ background: "#1746d1" }} />
                      <strong>{opportunity.label}</strong>
                    </div>
                    <small>{opportunity.overallOpportunityFit}% preliminary pathway match</small>
                  </div>
                ))}
              </div>}

              {hasIncomeOpportunityReport && <section className="uc-companion-snapshot">
                <h4>Readiness & Return Snapshot</h4>
                <dl>
                  <div>
                    <dt>Time</dt>
                    <dd>{currentResult.snapshot.time}</dd>
                  </div>
                  <div>
                    <dt>Energy</dt>
                    <dd>{currentResult.snapshot.energy}</dd>
                  </div>
                  <div>
                    <dt>Money</dt>
                    <dd>{currentResult.snapshot.money}</dd>
                  </div>
                  <div>
                    <dt>First result</dt>
                    <dd>{currentResult.snapshot.timeline}</dd>
                  </div>
                  <div>
                    <dt>Possible early monthly income</dt>
                  <dd>{currentResult.userIntent === "family_guidance" ? "Not applicable to guidance-only" : currentResult.snapshot.income}</dd>
                  </div>
                  <div>
                    <dt>Risk</dt>
                    <dd>{currentResult.snapshot.risk}</dd>
                  </div>
                </dl>
              </section>}

              {hasIncomeOpportunityReport && <section className="uc-companion-ideas">
                <h4>Possible starting ideas</h4>
                <ul>
                  {currentResult.paths.slice(0, 4).map((path) => (
                    <li key={path}>{path}</li>
                  ))}
                </ul>
              </section>}

              {hasIncomeOpportunityReport && <section className="uc-companion-why">
                <h4>Why this fits</h4>
                <ul>
                  {currentResult.reasons.slice(0, 4).map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </section>}

              <div className="uc-companion-guardrail">
                {currentResult.intentCopy.guardrail}
              </div>

              <form className="uc-companion-report-email-card" onSubmit={sendFreeReport} noValidate>
                <span>FREE REPORT</span>
                <h4>Send this report to your email</h4>
                <p>Save it for later and bring it to a UC expert for discussion.</p>
                <div className="uc-companion-report-fields">
                  <input ref={reportNameRef} value={reportName} onChange={(event) => setReportName(event.target.value)} onInput={(event) => setReportName(event.currentTarget.value)} placeholder="Your name" autoComplete="name" aria-label="Your name" />
                  <input ref={reportEmailRef} type="email" value={reportEmail} onChange={(event) => setReportEmail(event.target.value)} onInput={(event) => setReportEmail(event.currentTarget.value)} placeholder="Your email address" autoComplete="email" aria-label="Your email address" />
                </div>
                <label className="uc-companion-report-consent"><input type="checkbox" checked={expertDiscussion} onChange={(event) => setExpertDiscussion(event.target.checked)} /> Please have a UC expert follow up for discussion</label>
                <button type="submit" className="uc-companion-primary" disabled={reportSending}>{reportSending ? "Sending…" : "Email my free report"}</button>
                {reportMessage && <p className="uc-companion-report-message" aria-live="polite">{reportMessage}</p>}
              </form>

              {isProfileJourney && savedProfile && (
                <section className="uc-companion-contact-card">
                  <span>
                    <ContactSupport fontSize="small" />
                    UC connection
                  </span>
                  {contactRequest ? (
                    <>
                      <h4>Contact request: {contactRequest.status}</h4>
                      <p>
                        The UC team can review your saved profile and connect
                        you with an appropriate community or sector contact.
                      </p>
                    </>
                  ) : (
                    <>
                      <h4>{currentResult.intentCopy.contactHeading}</h4>
                      <p>
                        Add an optional note about the kind of introduction,
                        training, or mentor support you want.
                      </p>
                      <textarea
                        value={contactNotes}
                        onChange={(event) => setContactNotes(event.target.value)}
                        maxLength={1000}
                        placeholder={currentResult.intentCopy.contactPlaceholder}
                      />
                      <button
                        type="button"
                        className="uc-companion-primary"
                        onClick={submitContactRequest}
                        disabled={contactSubmitting}
                      >
                        <ContactSupport fontSize="small" />
                        {contactSubmitting ? "Sending…" : "Request a UC introduction"}
                      </button>
                    </>
                  )}
                </section>
              )}

              <div className="uc-companion-actions">
                {mentorPathway ? (
                  <button
                    type="button"
                    className="uc-companion-primary"
                    onClick={isProfileJourney ? startProfileAssessment : isAuthenticated ? startProfile : goToLogin}
                  >
                    {mentorPathway.connectionOption}
                  </button>
                ) : (
                  <button type="button" className="uc-companion-primary" onClick={goToPrimarySector}>
                    View matching sector
                  </button>
                )}
                <button
                  type="button"
                  className="uc-companion-secondary"
                  onClick={goToCommunityMeetups}
                >
                  <EventAvailable fontSize="small" />
                  View community meetups
                </button>
                {isProfileJourney ? (
                  <button
                    type="button"
                    className="uc-companion-secondary"
                    onClick={startProfileAssessment}
                  >
                    <Edit fontSize="small" />
                    Update profile answers
                  </button>
                ) : (
                  <button
                    type="button"
                    className="uc-companion-secondary"
                    onClick={isAuthenticated ? startProfile : goToLogin}
                  >
                    Build profile / Sign in
                  </button>
                )}
                <button
                  type="button"
                  className="uc-companion-ghost"
                  onClick={isProfileJourney ? startProfileAssessment : startExplore}
                >
                  <RestartAlt fontSize="small" />
                  Try again
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default OpportunityCompanion;
